import {
  ChevronLeft,
  ChevronRight,
  Star,
  Quote,
  BrainCircuit,
  Server,
  ShieldCheck,
  Plug,
  Check,
  Thermometer as ThermometerIcon,
  Activity,
  Wifi,
  BarChart3,
  Droplet,
  LineChart,
  HeartPulse,
  Clock,
  ArrowRight,
  X,
  Menu,
  ChevronDown,
} from "lucide-react";
import {
  motion,
  useScroll,
  useTransform,
  useInView,
  useMotionValue,
  useSpring,
  AnimatePresence,
  useMotionValueEvent,
} from "framer-motion";
import { useRef, useEffect, useState, createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../hooks/use-toast";

// ============ NOTIFICATION SYSTEM ============

// Notification Context
const NotificationContext = createContext();

// Custom Notification Hook
const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within NotificationProvider");
  }
  return context;
};

// Notification Bar Component
const NotificationBar = ({ notifications }) => {
  return (
    <AnimatePresence>
      {notifications.map((notification) => (
        <motion.div
          key={notification.id}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          className={`fixed top-20 left-0 right-0 mx-auto w-full max-w-2xl z-[9999] px-4`}
        >
          <div
            className={`rounded-lg shadow-lg p-4 flex items-start gap-3 border-l-4 ${
              notification.status
                ? "bg-green-50 border-green-500"
                : "bg-red-50 border-red-500"
            }`}
          >
            {/* Status Icon */}
            <div className="mt-1">
              {notification.status ? (
                <svg
                  className="w-5 h-5 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </div>

            {/* Message Content */}
            <div className="flex-1">
              <p
                className={`font-semibold text-sm ${
                  notification.status ? "text-green-800" : "text-red-800"
                }`}
                style={{ fontFamily: "Satoshi, sans-serif" }}
              >
                {notification.status ? "Success" : "Error"}
              </p>
              <p
                className={`text-sm mt-1 ${
                  notification.status ? "text-green-700" : "text-red-700"
                }`}
                style={{ fontFamily: "Satoshi, sans-serif" }}
              >
                {notification.message}
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={() => notification.onClose(notification.id)}
              className={`text-gray-400 hover:text-gray-600 flex-shrink-0 mt-1`}
            >
              <X size={18} />
            </button>
          </div>
        </motion.div>
      ))}
    </AnimatePresence>
  );
};

// Notification Provider Component
const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const showNotification = (message, status = true, duration = 5000) => {
    const id = Date.now();
    const newNotification = {
      id,
      message,
      status,
      onClose: (notificationId) => {
        setNotifications((prev) =>
          prev.filter((notif) => notif.id !== notificationId),
        );
      },
    };

    setNotifications((prev) => [...prev, newNotification]);

    // Auto-remove after duration
    if (duration > 0) {
      const timer = setTimeout(() => {
        setNotifications((prev) => prev.filter((notif) => notif.id !== id));
      }, duration);

      return () => clearTimeout(timer);
    }
  };

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      <NotificationBar notifications={notifications} />
      {children}
    </NotificationContext.Provider>
  );
};

// Scroll Reveal Animation Wrapper
const ScrollReveal = ({ children, className = "", delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1], delay }}
    className={className}
  >
    {children}
  </motion.div>
);

// Dynamic Navigation Bar Component
const DynamicNavbar = ({ onOpenModal }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    if (latest < 100) {
      setIsVisible(true);
    } else if (latest > lastScrollY) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
    setLastScrollY(latest);
  });

  const navLinks = [
    { name: "Home", href: "#hero-section" },
    { name: "Why MyBovine", href: "#why-mybovine" },
    { name: "Case Studies", href: "#case-studies" },
    { name: "News", href: "#news" },
  ];

  const handleNavClick = (e, href) => {
    e.preventDefault();
    if (href === "#hero-section") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.nav
      className="fixed top-0 w-full z-50 border-b border-black/5"
      style={{ backdropFilter: "blur(25px)", backgroundColor: "rgba(255,255,255,0.8)" }}
      initial={{ y: 0 }}
      animate={{ y: isVisible ? 0 : -100 }}
      transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
      data-testid="dynamic-navbar"
    >
      <div className="px-4 sm:px-6 md:px-10 xl:px-16 flex justify-between items-center w-full h-[64px] sm:h-[72px] md:h-[80px]">
        {/* Left — Logo */}
        <motion.a
          href="/"
          data-testid="navbar-logo"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          style={{ cursor: "pointer" }}
        >
          <img
            src="/Logo.svg"
            alt="MyBovine.ai"
            className="h-[30px] sm:h-[36px] md:h-[44px] w-auto"
          />
        </motion.a>

        {/* Right — CTA + Mobile toggle */}
        <div className="flex items-center gap-4">
          <motion.button
            onClick={onOpenModal}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="hidden sm:flex items-center justify-center text-[16px] font-medium bg-[#00BD7E] text-white px-[16px] py-[12px] rounded-[8px] hover:bg-[#00a86b] transition-colors"
            style={{ fontFamily: "Satoshi, sans-serif" }}
            data-testid="navbar-cta-btn"
          >
            Book a Farm Assessment
          </motion.button>

          {/* Mobile menu toggle */}
          <motion.button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="sm:hidden p-2"
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </motion.button>
        </div>
      </div>

      {/* Mobile dropdown */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="sm:hidden border-t border-black/5"
            style={{ backgroundColor: "rgba(255,255,255,0.95)", backdropFilter: "blur(25px)" }}
          >
            <div className="px-4 py-4">
              <motion.button
                onClick={() => {
                  onOpenModal();
                  setIsMobileMenuOpen(false);
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="text-[16px] font-medium bg-[#00BD7E] text-white px-[16px] py-[12px] rounded-[8px] hover:bg-[#00a86b] transition-colors w-full"
                style={{ fontFamily: "Satoshi, sans-serif" }}
              >
                Book a Farm Assessment
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// Modal Context
const ModalContext = createContext();

// Brochure Download Modal Component
const BrochureModal = ({ isOpen, onClose }) => {
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    city: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Invalid email format";
    if (!formData.city.trim()) newErrors.city = "City is required";
    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        "https://plm-apis.vanixtechnologies.com/api/brochure",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        },
      );

      const responseData = await response.json();
      const responseMessage =
        responseData?.message ||
        responseData?.description ||
        "Request processed successfully";

      if (response.ok) {
        showNotification(
          responseMessage || "Brochure download initiated. Check your email!",
          true,
        );

        // Reset form
        setFormData({
          name: "",
          email: "",
          city: "",
        });
        setErrors({});

        // Close modal after short delay
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        showNotification(
          responseMessage || "Something went wrong. Please try again.",
          false,
        );
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showNotification(
        error.message ||
          "Unable to download brochure. Please check your connection and try again.",
        false,
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="brochure-modal-overlay"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl p-8 w-full max-w-md shadow-xl border border-black/10 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="brochure-modal"
      >
        {/* Close Button */}
        <motion.button
          onClick={onClose}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition-colors"
          data-testid="brochure-modal-close-btn"
          type="button"
        >
          <X size={24} />
        </motion.button>

        {/* Header */}
        <h2
          className="text-[24px] font-bold text-black"
          style={{ fontFamily: "Satoshi, sans-serif" }}
        >
          Download Brochure
        </h2>
        <p
          className="text-[14px] text-gray-600 mt-2"
          style={{ fontFamily: "Satoshi, sans-serif" }}
        >
          Learn more about MyBovine's features and benefits
        </p>

        {/* Form */}
        <form className="flex flex-col gap-4 mt-6" onSubmit={handleSubmit}>
          {/* Name Input */}
          <div>
            <input
              type="text"
              name="name"
              placeholder="Your Full Name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full border rounded-xl px-4 py-3 text-[16px] outline-none transition-colors ${
                errors.name
                  ? "border-red-500 focus:border-red-500"
                  : "border-black/10 focus:border-[#00BD7E]"
              }`}
              style={{ fontFamily: "Satoshi, sans-serif" }}
              data-testid="brochure-input-name"
              disabled={isLoading}
            />
            {errors.name && (
              <p className="text-red-500 text-[12px] mt-1">{errors.name}</p>
            )}
          </div>

          {/* Email Input */}
          <div>
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full border rounded-xl px-4 py-3 text-[16px] outline-none transition-colors ${
                errors.email
                  ? "border-red-500 focus:border-red-500"
                  : "border-black/10 focus:border-[#00BD7E]"
              }`}
              style={{ fontFamily: "Satoshi, sans-serif" }}
              data-testid="brochure-input-email"
              disabled={isLoading}
            />
            {errors.email && (
              <p className="text-red-500 text-[12px] mt-1">{errors.email}</p>
            )}
          </div>

          {/* City Input */}
          <div>
            <input
              type="text"
              name="city"
              placeholder="City Name"
              value={formData.city}
              onChange={handleInputChange}
              className={`w-full border rounded-xl px-4 py-3 text-[16px] outline-none transition-colors ${
                errors.city
                  ? "border-red-500 focus:border-red-500"
                  : "border-black/10 focus:border-[#00BD7E]"
              }`}
              style={{ fontFamily: "Satoshi, sans-serif" }}
              data-testid="brochure-input-city"
              disabled={isLoading}
            />
            {errors.city && (
              <p className="text-red-500 text-[12px] mt-1">{errors.city}</p>
            )}
          </div>

          {/* Submit Button */}
          <motion.button
            type="submit"
            whileHover={{ scale: isLoading ? 1 : 1.05 }}
            whileTap={{ scale: isLoading ? 1 : 0.95 }}
            disabled={isLoading}
            className="bg-[#00BD7E] text-white text-[18px] font-medium rounded-xl px-6 py-3 w-full mt-2 hover:bg-[#00a86b] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ fontFamily: "Satoshi, sans-serif" }}
            data-testid="brochure-submit-btn"
          >
            {isLoading ? "Sending..." : "Download Brochure"}
          </motion.button>
        </form>
      </motion.div>
    </div>
  );
};

// Lead Capture Modal Component - Farm Assessment
const LeadCaptureModal = ({ isOpen, onClose }) => {
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    mobileNumber: "",
    address: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) newErrors.username = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Invalid email format";
    if (!formData.mobileNumber.trim())
      newErrors.mobileNumber = "Mobile number is required";
    if (!/^\d{10}$/.test(formData.mobileNumber.replace(/\D/g, "")))
      newErrors.mobileNumber = "Invalid mobile number";
    if (!formData.address.trim()) newErrors.address = "Address is required";
    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        "https://plm-apis.vanixtechnologies.com/api/contact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        },
      );

      const responseData = await response.json();
      const responseMessage =
        responseData?.message ||
        responseData?.description ||
        "Request processed successfully";

      if (response.ok) {
        showNotification(
          responseMessage ||
            "Farm assessment request submitted! Our team will reach out soon.",
          true,
        );

        // Reset form
        setFormData({
          username: "",
          email: "",
          mobileNumber: "",
          address: "",
        });
        setErrors({});

        // Close modal after short delay
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        showNotification(
          responseMessage || "Something went wrong. Please try again.",
          false,
        );
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showNotification(
        error.message ||
          "Unable to submit form. Please check your connection and try again.",
        false,
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="lead-capture-modal-overlay"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl p-8 w-full max-w-md shadow-xl border border-black/10 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-capture-modal"
      >
        {/* Close Button */}
        <motion.button
          onClick={onClose}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition-colors"
          data-testid="modal-close-btn"
          type="button"
        >
          <X size={24} />
        </motion.button>

        {/* Header */}
        <h2
          className="text-[24px] font-bold text-black"
          style={{ fontFamily: "Satoshi, sans-serif" }}
        >
          Farm Assessment
        </h2>
        <p
          className="text-[14px] text-gray-600 mt-2"
          style={{ fontFamily: "Satoshi, sans-serif" }}
        >
          Get a personalized assessment from our sales team
        </p>

        {/* Form */}
        <form className="flex flex-col gap-4 mt-6" onSubmit={handleSubmit}>
          {/* Name Input */}
          <div>
            <input
              type="text"
              name="username"
              placeholder="Your Full Name"
              value={formData.username}
              onChange={handleInputChange}
              className={`w-full border rounded-xl px-4 py-3 text-[16px] outline-none transition-colors ${
                errors.username
                  ? "border-red-500 focus:border-red-500"
                  : "border-black/10 focus:border-[#00BD7E]"
              }`}
              style={{ fontFamily: "Satoshi, sans-serif" }}
              data-testid="modal-input-username"
              disabled={isLoading}
            />
            {errors.username && (
              <p className="text-red-500 text-[12px] mt-1">{errors.username}</p>
            )}
          </div>

          {/* Email Input */}
          <div>
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full border rounded-xl px-4 py-3 text-[16px] outline-none transition-colors ${
                errors.email
                  ? "border-red-500 focus:border-red-500"
                  : "border-black/10 focus:border-[#00BD7E]"
              }`}
              style={{ fontFamily: "Satoshi, sans-serif" }}
              data-testid="modal-input-email"
              disabled={isLoading}
            />
            {errors.email && (
              <p className="text-red-500 text-[12px] mt-1">{errors.email}</p>
            )}
          </div>

          {/* Mobile Number Input */}
          <div>
            <input
              type="tel"
              name="mobileNumber"
              placeholder="Mobile Number (10 digits)"
              value={formData.mobileNumber}
              onChange={handleInputChange}
              className={`w-full border rounded-xl px-4 py-3 text-[16px] outline-none transition-colors ${
                errors.mobileNumber
                  ? "border-red-500 focus:border-red-500"
                  : "border-black/10 focus:border-[#00BD7E]"
              }`}
              style={{ fontFamily: "Satoshi, sans-serif" }}
              data-testid="modal-input-mobile"
              disabled={isLoading}
            />
            {errors.mobileNumber && (
              <p className="text-red-500 text-[12px] mt-1">
                {errors.mobileNumber}
              </p>
            )}
          </div>

          {/* Address Input */}
          <div>
            <textarea
              name="address"
              placeholder="Farm Address / Location"
              value={formData.address}
              onChange={handleInputChange}
              rows="3"
              className={`w-full border rounded-xl px-4 py-3 text-[16px] outline-none transition-colors resize-none ${
                errors.address
                  ? "border-red-500 focus:border-red-500"
                  : "border-black/10 focus:border-[#00BD7E]"
              }`}
              style={{ fontFamily: "Satoshi, sans-serif" }}
              data-testid="modal-input-address"
              disabled={isLoading}
            />
            {errors.address && (
              <p className="text-red-500 text-[12px] mt-1">{errors.address}</p>
            )}
          </div>

          {/* Submit Button */}
          <motion.button
            type="submit"
            whileHover={{ scale: isLoading ? 1 : 1.05 }}
            whileTap={{ scale: isLoading ? 1 : 0.95 }}
            disabled={isLoading}
            className="bg-[#00BD7E] text-white text-[18px] font-medium rounded-xl px-6 py-3 w-full mt-2 hover:bg-[#00a86b] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ fontFamily: "Satoshi, sans-serif" }}
            data-testid="modal-submit-btn"
          >
            {isLoading ? "Submitting..." : "Submit Assessment Request"}
          </motion.button>
        </form>
      </motion.div>
    </div>
  );
};

// Check Circle Icon SVG
const CheckCircleIcon = () => (
  <img
    src="https://customer-assets.emergentagent.com/job_cattle-intelligence/artifacts/x6g6aqu7_Checkcircle.svg"
    alt="Check"
    style={{ height: "32px" }}
  />
);

// Why MyBovine Section
const WhyMyBovineSection = () => {
  const pointers = [
    "Agentic AI for Smart App Interaction",
    "Patented Geo-Behavioral Intelligence Engine",
    "Ultra-Rugged Hardware Built for Harsh Indian Conditions",
  ];

  return (
    <section
      className="flex flex-col lg:flex-row justify-around w-full m-auto items-center px-4 sm:px-6 md:px-10 xl:px-16 py-12 md:py-[120px] gap-8 lg:gap-16"
      id="why-mybovine"
      data-testid="why-mybovine-section"
    >
      <ScrollReveal>
        <div className="flex flex-col lg:flex-row justify-between items-center w-full gap-8 lg:gap-16">
          {/* Left - Image */}
          <div className="w-full lg:w-[628px] h-[300px] md:h-[432px] rounded-[24px] overflow-hidden bg-gray-200">
            <img
              src="/Whymybovine image.png"
              alt="Why Vanix"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Right - Content */}
          <div className="flex flex-col items-start text-left max-w-2xl">
            {/* Badge */}
            <span
              className="uppercase rounded-[12px] px-[16px] py-[12px] border border-black/10 text-black text-[16px] font-medium"
              style={{ fontFamily: "Satoshi, sans-serif" }}
            >
              WHY MyBovine.ai?
            </span>

            {/* Heading */}
            <h2
              className="mt-[8px] text-[32px] md:text-[48px] font-bold text-black"
              style={{ fontFamily: "Satoshi, sans-serif" }}
            >
              Trusted Partner in Your{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2AA366] to-[#0084D1]">
                Agricultural
              </span>{" "}
              Growth
            </h2>

            {/* Pointers List */}
            <div className="mt-[32px] flex flex-col gap-[24px]">
              {pointers.map((text, index) => (
                <div
                  key={index}
                  className="rounded-[28px] w-fit bg-gradient-to-r from-[#2AA366]/10 to-[#0084D1]/10 border border-[#34CC80]/10 pl-[8px] py-[8px] pr-[16px] flex items-center gap-[8px]"
                >
                  <CheckCircleIcon />
                  <span
                    className="text-[16px] md:text-[18px] font-medium text-black"
                    style={{ fontFamily: "Satoshi, sans-serif" }}
                  >
                    {text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </ScrollReveal>
    </section>
  );
};

// Case Studies Section
const CaseStudiesSection = () => {
  const navigate = useNavigate();

  // Sample case studies for landing page preview
  const caseStudies = [
    {
      id: 1,
      title: "Shanti Sarovar - Spiritual Retreat Success",
      description:
        "Shanti Sarovar is a unique implementation site, a serene, spiritual retreat and caregiving center that maintains a high-pedigree micro-herd of 10 cattle.",
      image: "/case-study-1.svg",
    },
  ];

  return (
    <section
      className="flex flex-col px-4 sm:px-6 md:px-10 xl:px-16 py-12 md:py-16 bg-white"
      id="case-studies"
      data-testid="case-studies-section"
    >
      <ScrollReveal>
        {/* Top Content */}
        <div className="flex flex-col items-center text-center">
          {/* Badge */}
          <span
            className="uppercase rounded-[12px] px-[16px] py-[12px] border border-black/10 text-[16px] font-bold"
            style={{ fontFamily: "Satoshi, sans-serif" }}
          >
            CASE STUDIES
          </span>

          {/* Heading */}
          <h2
            className="mt-[10px] text-[32px] md:text-[48px] font-bold text-black"
            style={{ fontFamily: "Satoshi, sans-serif" }}
          >
            We Strive for{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2AA366] to-[#0084D1]">
              Perfection
            </span>
          </h2>
        </div>

        {/* Case Studies Grid */}
        <div className="mt-[48px] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 w-full">
          {caseStudies.map((caseStudy) => (
            <motion.div
              key={caseStudy.id}
              whileHover={{ y: -8 }}
              transition={{ duration: 0.3 }}
            >
              <div
                className="rounded-[16px] bg-white border border-black/10 overflow-hidden cursor-pointer shadow-sm hover:shadow-lg transition-all duration-300"
                data-testid="case-study-card"
                onClick={() => navigate(`/case-study/${caseStudy.id}`)}
              >
                {/* Image */}
                <div className="h-[200px] sm:h-[240px] w-full bg-gray-200 overflow-hidden">
                  <img
                    src={caseStudy.image}
                    alt={caseStudy.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>

                {/* Content */}
                <div className="p-[24px] flex flex-col gap-4">
                  <div>
                    <h3
                      className="text-[20px] md:text-[24px] font-medium text-black line-clamp-2"
                      style={{ fontFamily: "Satoshi, sans-serif" }}
                    >
                      {caseStudy.title}
                    </h3>
                    <p
                      className="text-[16px] md:text-[18px] font-medium text-black/55 mt-2 line-clamp-3"
                      style={{ fontFamily: "Satoshi, sans-serif" }}
                    >
                      {caseStudy.description}
                    </p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-[2px] text-black text-[18px] font-medium hover:text-[#2aa366] transition-colors bg-transparent border-none cursor-pointer p-0"
                    style={{ fontFamily: "Satoshi, sans-serif" }}
                    onClick={() => navigate(`/case-study-1`)}
                  >
                    Read More →
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* View All Button */}
        {/* <div className="mt-12 flex justify-center">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/case-studies")}
            className="px-8 py-3 bg-gradient-to-r from-[#2AA366] to-[#0084D1] text-white rounded-lg font-semibold hover:shadow-lg transition-shadow duration-300"
            style={{ fontFamily: "Satoshi, sans-serif" }}
          >
            View All Case Studies
          </motion.button>
        </div> */}
      </ScrollReveal>
    </section>
  );
};

// FAQ Data
const faqData = [
  {
    id: 1,
    question: "How do I purchase and install the system?",
    answer:
      "Book a farm assessment or email our sales team at sales@vanix.in. Our certified distributors provide on-site evaluation and tailored installation timelines.",
  },
  {
    id: 2,
    question: "What exactly is included in the purchase?",
    answer:
      "Your subscription includes the Smart Gateway, Cattle Collars, and integrated SIM connectivity. We offer flexible bulk subscription packages designed for professional and enterprise-scale herds.",
  },
  {
    id: 3,
    question: "What is the expected battery life of the collars?",
    answer:
      "Our ultra-low power hardware provides acontinuous 2-year battery lifecycle. The system is designed for zero maintenance with no manual charging required.",
  },
  {
    id: 4,
    question: "Can the hardware withstand extreme weather and farm conditions?",
    answer:
      "Every Cattle Node is IP67-rated for total waterproof and shock-resistant protection. Our sensors are engineered to endure the toughest industrial dairy environments 24/7.",
  },
  {
    id: 5,
    question: "How does MyBovine.ai reduce my daily manual labor?",
    answer:
      "Stop spending hours manually searching for sick cows or silent heat cycles. MyBovine.ai identifies these critical events automatically, giving you your time and freedom back.",
  },
];

// FAQ / Support Centre Section
const FAQSection = () => {
  const [openId, setOpenId] = useState(null);
  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  return (
    <section
      className="w-full px-4 sm:px-6 md:px-10 xl:px-16 py-12 md:py-[120px] bg-[#f9fafb]"
      data-testid="faq-section"
    >
      <div className="flex flex-col lg:flex-row gap-8 sm:gap-10 lg:gap-24 items-start w-full">
        {/* Left - Heading */}
        <ScrollReveal className="lg:max-w-[400px] w-full shrink-0">
          {/* <span
            className="uppercase"
            style={{
              fontFamily: "Satoshi, sans-serif",
              fontWeight: 500,
              fontSize: "16px",
              color: "#000000",
              padding: "12px 16px",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              borderRadius: "8px",
            }}
            data-testid="testimonials-badge"
          >
            SUPPORT CENTRE
          </span> */}
          <h2
            className="mt-4 text-[24px] sm:text-[32px] md:text-[40px] lg:text-[48px] font-bold text-black leading-tight"
            style={{ fontFamily: "Satoshi, sans-serif" }}
          >
            Frequently Asked Questions
          </h2>
          <p
            className="mt-4 text-[16px] md:text-[18px] text-[#6B7280]"
            style={{ fontFamily: "Satoshi, sans-serif", fontWeight: 400 }}
          >
            Quick answers to common questions about MyBovine.ai technology.
          </p>
        </ScrollReveal>

        {/* Right - Accordion */}
        <div className="w-full flex flex-col gap-4">
          {faqData.map((item) => (
            <motion.div
              key={item.id}
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-[16px] border border-black/[0.08] overflow-hidden shadow-sm"
              data-testid={`faq-item-${item.id}`}
            >
              <button
                onClick={() => toggle(item.id)}
                className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
              >
                <span
                  className="text-[16px] md:text-[18px] font-medium text-[#111827]"
                  style={{ fontFamily: "Satoshi, sans-serif" }}
                >
                  {item.question}
                </span>
                <motion.span
                  animate={{ rotate: openId === item.id ? 180 : 0 }}
                  transition={{ duration: 0.25 }}
                  className="ml-4 shrink-0"
                >
                  <ChevronDown size={20} color="#0084D1" />
                </motion.span>
              </button>
              <AnimatePresence initial={false}>
                {openId === item.id && (
                  <motion.div
                    key="answer"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    style={{ overflow: "hidden" }}
                  >
                    <p
                      className="px-6 pb-5 text-[15px] md:text-[16px] text-[#6B7280]"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        fontWeight: 400,
                      }}
                    >
                      {item.answer}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Let's Talk Section
const LetsTalkSection = ({ onOpenModal }) => {
  const imgGeminiGeneratedImage =
    "http://localhost:3845/assets/87081d541f88e277417fb5a9e3811ae2f7b14aec.png";

  return (
    <section
      className="relative w-full bg-white"
      data-testid="lets-talk-section"
      data-node-id="1693:1724"
    >
      {/* Background Image - 650px height */}
      <div
        className="absolute top-0 left-0 right-0 h-[450px] sm:h-[550px] md:h-[650px] lg:h-[750px] w-full overflow-hidden pointer-events-none"
        data-node-id="1693:1725"
      >
        <img
          alt=""
          className="w-full h-full object-cover"
          src="/lets-talks-bg.svg"
        />
      </div>

      {/* Content Container */}
      <div
        className="relative content-stretch flex flex-col gap-4 sm:gap-5 md:gap-[24px] items-center md:items-end min-h-[450px] sm:min-h-[550px] md:min-h-[650px] lg:min-h-[750px] pt-10 sm:pt-12 md:pt-[40px] px-4 sm:px-6 md:px-10 xl:px-16 z-20 pb-10 sm:pb-12 md:pb-[64px]"
        data-node-id="1693:1726"
      >
        {/* Header Section */}
        <div
          className="content-stretch flex flex-col items-center md:items-end relative shrink-0 w-full"
          data-node-id="1693:1727"
        >
          <div
            className="content-stretch flex flex-col gap-[12px] items-center md:items-end relative shrink-0 w-full"
            data-node-id="1693:1728"
          >
            <span
              style={{
                fontFamily: "Satoshi, sans-serif",
                fontWeight: 500,
                fontSize: "16px",
                color: "#000000",
                padding: "12px 16px",
                border: "1px solid rgba(0, 0, 0, 0.1)",
                borderRadius: "8px",
              }}
              data-testid="testimonials-badge"
            >
              LET'S TALK!!
            </span>

            {/* Heading */}
            <h2
              className="font-bold leading-tight sm:leading-[40px] md:leading-[50px] relative shrink-0 text-[28px] sm:text-[36px] md:text-[44px] lg:text-[48px] text-black text-center md:text-right w-auto"
              style={{
                fontFamily: "'Satoshi Variable Bold', 'Satoshi', sans-serif",
              }}
              data-node-id="1693:1731"
            >
              <span className="leading-[50px]">Ready to </span>
              <span className="bg-clip-text bg-gradient-to-r from-[#2aa366] to-[#0084d1] text-transparent leading-[50px]">
                Modernise
              </span>
              <span className="leading-[50px]"> Your Farm?</span>
            </h2>
          </div>
        </div>

        {/* Buttons & Footer Container */}
        <div
          className="content-stretch flex flex-col gap-[14px] items-end md:items-end relative shrink-0 w-full sm:w-[339px]"
          data-node-id="1693:1732"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onOpenModal}
            className="bg-[#00bd7e] content-stretch flex items-center justify-center px-[24px] py-[12px] relative rounded-[12px] shrink-0 hover:bg-[#00a86b] transition-colors"
            style={{
              fontFamily: "'Satoshi Variable Medium', 'Satoshi', sans-serif",
            }}
            data-testid="chat-with-us-btn"
            data-node-id="1693:1734"
          >
            <p
              className="flex flex-col font-medium justify-center leading-[1.45] relative shrink-0 text-[14px] sm:text-[16px] md:text-[18px] text-center text-white tracking-[-0.09px] whitespace-nowrap"
              data-node-id="1693:1735"
            >
              Book a Free Farm Assessment
            </p>
          </motion.button>

          <div
            className="content-stretch flex flex-col items-center relative shrink-0 w-full"
            data-node-id="1693:1738"
          >
            <p
              className="flex flex-col font-medium justify-center leading-[20px] relative shrink-0 text-gray-700 text-[14px] text-center whitespace-nowrap"
              style={{
                fontFamily: "'Satoshi Variable Medium', 'Satoshi', sans-serif",
              }}
            >
              Join now 10,000+ farmers making their farm smarter.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Data - Edit here
const testimonialsData = [
  {
    id: 1,
    quote:
      "'Since MyBovine.ai, I never worry about missed heats or illnesses Proactive alerts saved our highest-yielding cattle and maximized our seasonal breeding.",
    author: "Ramu Annamalai",
    location: "Village, Telangana.",
    imageSrc: "/testi.png",
  },
  {
    id: 2,
    quote:
      "'Since MyBovine.ai, I never worry about missed heats or illnesses Proactive alerts saved our highest-yielding cattle and maximized our seasonal breeding.",
    author: "Ramu Annamalai",
    location: "Village, Telangana.",
    imageSrc: "/testi2.png",
  },
  {
    id: 3,
    quote:
      "'Since MyBovine.ai, I never worry about missed heats or illnesses Proactive alerts saved our highest-yielding cattle and maximized our seasonal breeding.",
    author: "Ramu Annamalai",
    location: "Village, Telangana.",
    imageSrc: "/testi.png",
  },
];

// Testimonial Card Component
const TestimonialCard = ({ testimonial, isActive, cardWidth }) => {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.3 }}
      className={`flex-shrink-0 rounded-2xl p-3 sm:p-4 flex flex-col md:flex-row items-center gap-3 sm:gap-4 md:gap-8 transition-all duration-500 h-auto md:h-[475px] ${
        isActive
          ? "bg-[#F9FAFB] shadow-md scale-100 opacity-100"
          : "bg-white scale-95 opacity-40"
      }`}
      style={{ width: `${cardWidth}px` }}
    >
      {/* Left - Image */}
      <div className="w-full md:flex-1 h-[180px] sm:h-[250px] md:h-full rounded-xl overflow-hidden bg-black">
        <img
          src={testimonial.imageSrc}
          alt={`${testimonial.author} testimonial`}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Right - Content */}
      <div className="w-full md:flex-1 flex flex-col justify-center gap-2 sm:gap-3 md:gap-4">
        {/* Quote Icon */}
        <Quote
          className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10"
          color="#2AA366"
        />

        {/* Quote Text */}
        <p
          className="text-sm sm:text-base md:text-md lg:text-[20px] text-[#111827] leading-snug md:leading-[1.4]"
          style={{ fontFamily: "Satoshi, sans-serif", fontWeight: 400 }}
        >
          {testimonial.quote}
        </p>

        {/* Author */}
        <div>
          <p
            className="text-sm sm:text-base md:text-[18px] font-medium text-[#111827]"
            style={{ fontFamily: "Satoshi, sans-serif" }}
          >
            {testimonial.author}
          </p>
          <p
            className="text-xs md:text-[14px] text-[#6B7280] mt-0.5 sm:mt-1"
            style={{ fontFamily: "Satoshi, sans-serif" }}
          >
            {testimonial.location}
          </p>
        </div>

        {/* Stars */}
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              size={14}
              className="sm:w-4 sm:h-4"
              fill="#EAB308"
              color="#EAB308"
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// Testimonials Carousel Component
const TestimonialsCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(1);
  const [cardWidth, setCardWidth] = useState(854);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );
  const trackRef = useRef(null);

  useEffect(() => {
    const updateWidth = () => {
      const w = window.innerWidth;
      setWindowWidth(w);
      if (w < 480) setCardWidth(w - 48);
      else if (w < 640) setCardWidth(w - 64);
      else if (w < 768) setCardWidth(480);
      else if (w < 1024) setCardWidth(680);
      else setCardWidth(854);
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const handlePrev = () => {
    setActiveIndex((prev) =>
      prev === 0 ? testimonialsData.length - 1 : prev - 1,
    );
  };

  const handleNext = () => {
    setActiveIndex((prev) =>
      prev === testimonialsData.length - 1 ? 0 : prev + 1,
    );
  };

  const isMobile = windowWidth < 640;
  const gap = isMobile ? 16 : 32;
  const offset =
    -(activeIndex * (cardWidth + gap)) + (windowWidth - cardWidth) / 2;

  const maskGradient = isMobile
    ? "linear-gradient(to right, transparent, black 5%, black 95%, transparent)"
    : "linear-gradient(to right, transparent, black 15%, black 85%, transparent)";

  return (
    <div className="w-full relative py-6 sm:py-12">
      {/* Left Arrow */}
      <motion.button
        onClick={handlePrev}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        className="absolute left-1 sm:left-2 md:left-8 top-1/2 -translate-y-1/2 z-10 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full border border-black/10 bg-white/90 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:shadow-md transition-shadow"
        data-testid="testimonial-prev-btn"
      >
        <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" color="#111827" />
      </motion.button>

      {/* Carousel Track with Fade Effect */}
      <div
        className="overflow-hidden"
        style={{
          maskImage: maskGradient,
          WebkitMaskImage: maskGradient,
        }}
      >
        <motion.div
          ref={trackRef}
          className="flex flex-row items-center"
          style={{ gap: `${gap}px` }}
          animate={{ x: offset }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {testimonialsData.map((testimonial, index) => (
            <TestimonialCard
              key={testimonial.id}
              testimonial={testimonial}
              isActive={index === activeIndex}
              cardWidth={cardWidth}
            />
          ))}
        </motion.div>
      </div>

      {/* Right Arrow */}
      <motion.button
        onClick={handleNext}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        className="absolute right-1 sm:right-2 md:right-8 top-1/2 -translate-y-1/2 z-10 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full border border-black/10 bg-white/90 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:shadow-md transition-shadow"
        data-testid="testimonial-next-btn"
      >
        <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" color="#111827" />
      </motion.button>

      {/* Dots Indicator */}
      <div className="flex justify-center gap-2 mt-4 sm:mt-6">
        {testimonialsData.map((_, index) => (
          <motion.button
            key={index}
            onClick={() => setActiveIndex(index)}
            whileHover={{ scale: 1.2 }}
            className={`h-2 rounded-full transition-all ${
              index === activeIndex ? "bg-[#0084D1] w-6" : "bg-gray-300 w-2"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// Impact Metrics Data - Edit values here
const impactMetricsData = [
  {
    value: 10,
    suffix: "K+",
    title: "ACTIVE NODES",
    subtext:
      "Connected cattle currently monitored across our growing digital dairy network.",
  },
  {
    value: 15,
    suffix: "K+",
    title: "HEATS DETECTED",
    subtext:
      "Accurately identified breeding cycles ensuring optimal calving intervals and productivity.",
  },
  {
    value: 2.5,
    suffix: "K+",
    title: "HEALTH ALERTS",
    subtext:
      "Early medical warnings issued before physical symptoms of illness appeared.",
  },
  {
    value: 1.2,
    suffix: "K+",
    title: "CATTLE SAVED",
    subtext:
      "High-value assets protected through real-time intervention and proactive medical alerts.",
  },
  {
    value: 25,
    suffix: "M+",
    title: "LOSSES PREVENTED",
    subtext:
      "Revenue protected by eliminating missed heat cycles and reducing cattle mortality.",
  },
];

// Animated Counter Component
const AnimatedCounter = ({ value, suffix = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, { duration: 2000, bounce: 0 });
  const displayValue = useMotionValue("0");

  useEffect(() => {
    if (isInView) {
      motionValue.set(value);
    }
  }, [isInView, value, motionValue]);

  useEffect(() => {
    const unsubscribe = springValue.on("change", (latest) => {
      displayValue.set(Math.round(latest).toString());
    });
    return unsubscribe;
  }, [springValue, displayValue]);

  return (
    <span ref={ref} className="flex items-baseline justify-center">
      <motion.span
        className="text-[24px] sm:text-[32px] md:text-[44px] lg:text-[56px] font-bold text-[#0084D1] leading-tight"
        style={{ fontFamily: "Satoshi, sans-serif" }}
      >
        {isInView ? <motion.span>{displayValue}</motion.span> : "0"}
      </motion.span>
      <span
        className="text-[24px] sm:text-[32px] md:text-[44px] lg:text-[56px] font-bold text-[#0084D1] leading-tight"
        style={{ fontFamily: "Satoshi, sans-serif" }}
      >
        {suffix}
      </span>
    </span>
  );
};

// Impact Metrics Card Component
const ImpactMetricCard = ({ value, suffix, title, subtext, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.06 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
      className="bg-white border border-black/[0.08] rounded-2xl shadow-sm p-6 flex flex-col items-center text-center gap-2 cursor-pointer"
      data-testid={`impact-metric-card-${index + 1}`}
    >
      <AnimatedCounter value={value} suffix={suffix} />
      <h3
        className="text-[14px] sm:text-[16px] md:text-[18px]"
        style={{
          fontFamily: "Satoshi, sans-serif",
          fontWeight: 500,
          color: "#0E131E",
        }}
      >
        {title}
      </h3>
      <p
        className="text-[12px] sm:text-[13px] md:text-[14px]"
        style={{
          fontFamily: "Satoshi, sans-serif",
          fontWeight: 400,
          color: "#6B7280",
        }}
      >
        {subtext}
      </p>
    </motion.div>
  );
};

// Impact Metrics Section
const ImpactMetricsSection = () => {
  return (
    <section
      className="w-full px-4 sm:px-6 md:px-10 xl:px-16 py-12 md:py-20 bg-[#f9fafb]"
      data-testid="impact-metrics-section"
    >
      <ScrollReveal>
        {/* Header */}
        {/* <div className="flex flex-col items-center gap-2 mb-8 md:mb-12 text-center">
          <span
            style={{
              fontFamily: "Satoshi, sans-serif",
              fontWeight: 500,
              fontSize: "16px",
              color: "#000000",
              padding: "12px 16px",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              borderRadius: "8px",
            }}
            data-testid="testimonials-badge"
          >
            IMPACT & ROI
          </span>
        </div> */}

        {/* Metrics Grid */}
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6 w-full"
          data-testid="impact-metrics-grid"
        >
          {impactMetricsData.map((metric, index) => (
            <ImpactMetricCard key={index} {...metric} index={index} />
          ))}
        </div>
      </ScrollReveal>
    </section>
  );
};

// Card Stacking Section Data
const stackingCardsData = [
  {
    id: 1,
    icon: ThermometerIcon,
    title: "Real Time Heat Detection",
    description:
      "Effective heat detection is crucial for optimizing livestock breeding. By monitoring temperature changes in animals, farmers can identify the best times for mating, ensuring higher conception rates. Utilizing advanced technology like heat detection collars or sensors can streamline this process, making it easier to manage herds and improve overall productivity.",
    image: "frontend\\public\\why-vanix-img1.svg",
    pointers: null,
  },
  {
    id: 2,
    icon: Activity,
    title: "Detect health issues Before symptoms appear",
    description:
      "Monitor cattle health effectively with advanced tracking systems. Utilize digital dashboards to visualize health metrics and ensure your herd stays healthy and productive.",
    image: "frontend\\public\\why-vanix-img2.svg",
    pointers: null,
  },
  {
    id: 3,
    icon: Wifi,
    title: "24/7 Wireless Monitoring",
    description: "Be with your family, while we watch the herd.",
    image: "frontend\\public\\why-vanix-img3.svg",
    pointers: [
      "24/7 Virtual Oversight.",
      "Notifications only when it matters.",
      "Less stress, more control.",
    ],
  },
  {
    id: 4,
    icon: BarChart3,
    title: "Cattle Behaviour Monitoring",
    description:
      "Monitor cattle behavior effectively with advanced tracking systems. Utilize sensors and data analytics to gain insights into their movements and interactions, ensuring their well-being and optimizing farm management.",
    image: "frontend\\public\\why-vanix-img4.svg",
    pointers: ["Rumination", "Feeding", "High or Low Activity"],
  },
];

// Check Icon SVG
const CheckIconSVG = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M10 3L4.5 8.5L2 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Single Stacking Card Component with sticky positioning
const StackingCard = ({ card, index }) => {
  const topValues = [
    "top-[100px]",
    "top-[116px]",
    "top-[132px]",
    "top-[148px]",
  ];

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.3 }}
      className={`sticky ${topValues[index]} w-full`}
      style={{ zIndex: index + 1 }}
      data-testid={`stacking-card-${card.id}`}
    >
      <div className="w-full flex flex-col md:flex-row rounded-[16px] bg-[#004F7D] overflow-hidden shadow-lg min-h-[auto] md:min-h-[480px]">
        {/* Left Content */}
        <div className="w-full md:w-1/2 p-6 sm:p-8 md:p-[48px] flex flex-col justify-start">
          {/* Icon Box */}
          <div
            className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-start justify-start"
            style={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}
          >
            <card.icon size={24} color="#ffffff" />
          </div>

          {/* Header */}
          <h3
            className="mt-4 md:mt-6 text-xl sm:text-2xl md:text-[30px] font-medium text-white"
            style={{ fontFamily: "Satoshi, sans-serif" }}
          >
            {card.title}
          </h3>

          {/* Description */}
          <p
            className="mt-3 md:mt-4 text-sm sm:text-base md:text-[18px] text-[#94A3B8]"
            style={{ fontFamily: "Satoshi, sans-serif", fontWeight: 400 }}
          >
            {card.description}
          </p>

          {/* Pointers List (if any) */}
          {card.pointers && (
            <div className="mt-4 md:mt-6 flex flex-col gap-2 md:gap-3">
              {card.pointers.map((pointer, idx) => (
                <div key={idx} className="flex flex-row items-center gap-2">
                  <div className="text-white">
                    <CheckIconSVG />
                  </div>
                  <span
                    className="text-sm md:text-[16px] font-medium"
                    style={{
                      fontFamily: "Satoshi, sans-serif",
                      color: "rgba(255, 255, 255, 0.6)",
                    }}
                  >
                    {pointer}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Image */}
        <div className="w-full md:w-1/2 h-[200px] sm:h-[280px] md:h-auto relative">
          <img
            src={card.image}
            alt={card.title}
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    </motion.div>
  );
};

// Card Stacking Section
const CardStackingSection = () => {
  return (
    <section
      className="w-full px-4 sm:px-6 md:px-10 xl:px-16 py-12 md:py-20 relative bg-[#f9fafb]"
      data-testid="card-stacking-section"
    >
      <div className="max-w-full mx-auto">
        {/* Stacking Cards Container */}
        <div className="w-full flex flex-col gap-6 md:gap-8 relative">
          {stackingCardsData.map((card, index) => (
            <StackingCard key={card.id} card={card} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// SVG Icon Components
const ClockIcon = () => (
  <svg width="24" height="24" viewBox="0 0 20 20" fill="black">
    <path d="M10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2ZM0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10ZM10 5C10.5523 5 11 5.44772 11 6V9.58579L13.7071 12.2929C14.0976 12.6834 14.0976 13.3166 13.7071 13.7071C13.3166 14.0976 12.6834 14.0976 12.2929 13.7071L9.29289 10.7071C9.10536 10.5196 9 10.2652 9 10V6C9 5.44772 9.44771 5 10 5Z" />
  </svg>
);

const CubeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 18 20" fill="black">
    <path d="M8.55279 0.105573C8.83431 -0.0351909 9.16569 -0.0351909 9.44721 0.105573L17.4472 4.10557C17.786 4.27496 18 4.62123 18 5V15C18 15.3788 17.786 15.725 17.4472 15.8944L9.44721 19.8944C9.16569 20.0352 8.83431 20.0352 8.55279 19.8944L0.552786 15.8944C0.214002 15.725 0 15.3788 0 15V5C0 4.62123 0.214002 4.27496 0.552786 4.10557L8.55279 0.105573ZM2 6.61803L8 9.61803V17.382L2 14.382V6.61803ZM10 17.382L16 14.382V6.61803L10 9.61803V17.382ZM9 7.88197L14.7639 5L9 2.11803L3.23607 5L9 7.88197Z" />
  </svg>
);

const CheckTickIcon = () => (
  <svg width="19.2" height="19.2" viewBox="0 0 19.2 19.2" fill="white">
    <path d="M18.2482 6.81812L8.61825 16.4481C8.39323 16.673 8.08812 16.7993 7.77 16.7993C7.45187 16.7993 7.14677 16.673 6.92175 16.4481L1.55025 11.0481C1.3256 10.8231 1.19943 10.5182 1.19943 10.2002C1.19943 9.88231 1.3256 9.57736 1.55025 9.35237L3.05025 7.85237C3.27514 7.62841 3.57961 7.50267 3.897 7.50267C4.21439 7.50267 4.51885 7.62841 4.74375 7.85237L7.8 10.8164L15.057 3.64862C15.2819 3.42443 15.5865 3.29855 15.9041 3.29855C16.2217 3.29855 16.5263 3.42443 16.7512 3.64862L18.2475 5.11412C18.36 5.22563 18.4494 5.35831 18.5103 5.50451C18.5713 5.65072 18.6028 5.80755 18.6028 5.96596C18.6029 6.12438 18.5716 6.28124 18.5107 6.4275C18.4499 6.57375 18.3607 6.70652 18.2482 6.81812Z" />
  </svg>
);

// Footer Component
const Footer = () => {
  const { showNotification } = useNotification();
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [isNewsletterLoading, setIsNewsletterLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();

    if (!newsletterEmail.trim()) {
      showNotification("Please enter a valid email address.", false);
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newsletterEmail)) {
      showNotification("Please enter a valid email address.", false);
      return;
    }

    setIsNewsletterLoading(true);

    try {
      // Example: Replace with your actual newsletter API endpoint
      const response = await fetch(
        "https://plm-apis.vanixtechnologies.com/api/newsletter",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: newsletterEmail }),
        },
      );

      const responseData = await response.json();
      const responseMessage =
        responseData?.message ||
        responseData?.description ||
        "Subscribed successfully";

      if (response.ok) {
        showNotification(
          responseMessage ||
            "You have successfully subscribed to our newsletter!",
          true,
        );
        setNewsletterEmail("");
      } else {
        showNotification(
          responseMessage || "Failed to subscribe. Please try again.",
          false,
        );
      }
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      showNotification(
        error.message ||
          "Unable to subscribe. Please check your connection and try again.",
        false,
      );
    } finally {
      setIsNewsletterLoading(false);
    }
  };

  return (
    <footer
      className="w-full flex flex-col gap-10 items-center px-4 sm:px-6 md:px-10 xl:px-16 py-12 md:py-16"
      style={{ backgroundColor: "#0f141f" }}
      data-testid="footer"
    >
      {/* Main Content Holder */}
      <div
        className="w-full rounded-xl p-8 md:p-8 flex flex-col md:flex-row justify-between items-start gap-10 md:gap-8"
        style={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}
        data-testid="footer-content-card"
      >
        {/* Col 1 - Company */}
        <div className="flex flex-col gap-6">
          {/* MyBovine Logo */}
          <motion.div
            className="flex items-baseline gap-1"
            whileHover={{
              scale: 1.2,
            }}
            whileTap={{ scale: 0.92 }}
            style={{
              cursor: "pointer",
            }}
          >
            <motion.span
              animate={{
                textShadow: [
                  "0 0 0px rgba(42, 163, 102, 0), 0 0 10px rgba(0, 189, 126, 0.1)",
                  "0 0 12px rgba(42, 163, 102, 0.5), 0 0 24px rgba(0, 189, 126, 0.4), 0 0 40px rgba(42, 163, 102, 0.2)",
                  "0 0 0px rgba(42, 163, 102, 0), 0 0 10px rgba(0, 189, 126, 0.1)",
                ],
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              style={{
                fontFamily: "Lexend, sans-serif",
                fontWeight: 700,
                fontSize: "31px",
                color: "#ffffff",
                letterSpacing: "-1.5px",
                lineHeight: "1",
              }}
            >
              MyBovine
            </motion.span>
            <motion.span
              animate={{
                textShadow: [
                  "0 0 0px rgba(0, 132, 209, 0), 0 0 12px rgba(94, 179, 246, 0.1)",
                  "0 0 14px rgba(0, 132, 209, 0.6), 0 0 28px rgba(94, 179, 246, 0.5), 0 0 44px rgba(0, 132, 209, 0.25)",
                  "0 0 0px rgba(0, 132, 209, 0), 0 0 12px rgba(94, 179, 246, 0.1)",
                ],
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.15,
              }}
              style={{
                fontFamily: "Lexend, sans-serif",
                fontWeight: 300,
                fontSize: "31px",
                color: "#0084d1",
                letterSpacing: "-1.5px",
                lineHeight: "1",
              }}
            >
              .ai
            </motion.span>
          </motion.div>

          {/* Company Links */}
          <div className="flex flex-col gap-3">
            <span
              className="uppercase"
              style={{
                fontFamily: "Satoshi, sans-serif",
                fontWeight: 700,
                fontSize: "14px",
                color: "#F5FFFA",
                letterSpacing: "0.5px",
              }}
            >
              Our Company
            </span>
            <motion.a
              href="https://vanixtechnologies.com/about-us"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              style={{
                fontFamily: "Geist, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                color: "rgba(255, 255, 255, 0.6)",
                display: "inline-block",
              }}
            >
              About us
            </motion.a>
            <motion.a
              href="https://vanixtechnologies.com/faqs"
              target="_blank"
              whileHover={{ scale: 1.05 }}
              style={{
                fontFamily: "Geist, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                color: "rgba(255, 255, 255, 0.6)",
                display: "inline-block",
              }}
            >
              FAQs
            </motion.a>
          </div>
        </div>

        {/* Col 2 - Newsroom */}
        <div className="flex flex-col gap-3">
          <span
            className="uppercase"
            style={{
              fontFamily: "Satoshi, sans-serif",
              fontWeight: 700,
              fontSize: "14px",
              color: "#F5FFFA",
              letterSpacing: "0.5px",
            }}
          >
            Press
          </span>
          <motion.a
            href="https://vanixtechnologies.com/press"
            target="_blank"
            whileHover={{ scale: 1.05 }}
            style={{
              fontFamily: "Geist, sans-serif",
              fontWeight: 400,
              fontSize: "14px",
              color: "rgba(255, 255, 255, 0.6)",
              display: "inline-block",
            }}
          >
            Case studies
          </motion.a>
          <motion.a
            href="https://vanixtechnologies.com/events"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            style={{
              fontFamily: "Geist, sans-serif",
              fontWeight: 400,
              fontSize: "14px",
              color: "rgba(255, 255, 255, 0.6)",
              display: "inline-block",
            }}
          >
            Events
          </motion.a>
          <motion.a
            href="https://vanixtechnologies.com/blogs"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            style={{
              fontFamily: "Geist, sans-serif",
              fontWeight: 400,
              fontSize: "14px",
              color: "rgba(255, 255, 255, 0.6)",
              display: "inline-block",
            }}
          >
            Blogs
          </motion.a>
        </div>

        {/* Col 3 - Connect & Download */}
        <div className="flex flex-col gap-10">
          {/* Let's Connect */}
          <div className="flex flex-col gap-3">
            <span
              className="uppercase"
              style={{
                fontFamily: "Satoshi, sans-serif",
                fontWeight: 700,
                fontSize: "14px",
                color: "#F5FFFA",
                letterSpacing: "0.5px",
              }}
            >
              Let's connect
            </span>
            <div className="flex gap-4">
              {/* YouTube */}
              <a
                href="https://youtube.com/@vanixtechnologiesofficial?si=m84J-RyFofkFKa4N"
                target="_blank"
                style={{ opacity: 0.7, transition: "opacity 0.3s" }}
                onMouseEnter={(e) => (e.target.style.opacity = "1")}
                onMouseLeave={(e) => (e.target.style.opacity = "0.7")}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="white"
                  stroke="white"
                  strokeWidth="1"
                >
                  <path d="M23 9.71a8.5 8.5 0 0 0-.91-4.13 2.92 2.92 0 0 0-1.72-1.12A69.28 69.28 0 0 0 12 4a69.28 69.28 0 0 0-8.37.46 2.92 2.92 0 0 0-1.72 1.12A8.5 8.5 0 0 0 1 9.71a57.61 57.61 0 0 0 0 4.58 8.5 8.5 0 0 0 .91 4.13 2.92 2.92 0 0 0 1.72 1.12A69.28 69.28 0 0 0 12 20a69.28 69.28 0 0 0 8.37-.46 2.92 2.92 0 0 0 1.72-1.12 8.5 8.5 0 0 0 .91-4.13 57.61 57.61 0 0 0 0-4.58zM9.74 14.85V9.15l5.15 2.85-5.15 2.85z" />
                </svg>
              </a>
              {/* Instagram */}
              <a
                href="https://www.instagram.com/vanixtechnologies?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=="
                target="_blank"
                style={{ opacity: 0.7, transition: "opacity 0.3s" }}
                onMouseEnter={(e) => (e.target.style.opacity = "1")}
                onMouseLeave={(e) => (e.target.style.opacity = "0.7")}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="1.5"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="18" cy="6" r="1.5" fill="white" />
                </svg>
              </a>
              {/* LinkedIn */}
              <a
                href="https://www.linkedin.com/company/vanixtechnologies/"
                target="_blank"
                style={{ opacity: 0.7, transition: "opacity 0.3s" }}
                onMouseEnter={(e) => (e.target.style.opacity = "1")}
                onMouseLeave={(e) => (e.target.style.opacity = "0.7")}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Download MyBovine.ai */}
          <div className="flex flex-col gap-3">
            <span
              style={{
                fontFamily: "Satoshi, sans-serif",
                fontWeight: 700,
                fontSize: "14px",
                color: "#F5FFFA",
                letterSpacing: "0.5px",
              }}
            >
              Download MyBovine.ai
            </span>
            <div className="flex gap-4">
              {/* App Store */}
              <motion.a
                href="https://apps.apple.com/in/app/my-bovine-ai/id6758000909"
                target="_blank"
                whileHover={{ scale: 1.05 }}
                style={{
                  display: "inline-flex",
                  height: "40px",
                  width: "120px",
                }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_cattle-intelligence/artifacts/q3qd7nug_appstorebutton.svg"
                  alt="Download on App Store"
                  className="h-[36px] sm:h-[40px]"
                />
              </motion.a>
              {/* Google Play */}
              <motion.a
                target="_blank"
                href="https://play.google.com/store/apps/details?id=com.vanix.cattle.vanix_cattle&pcampaignid=web_share"
                whileHover={{ scale: 1.05 }}
                style={{
                  display: "inline-flex",
                  height: "40px",
                  width: "120px",
                }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_cattle-intelligence/artifacts/zffwa1x8_googleplaystorebutton.svg"
                  alt="Get it on Google Play"
                  className="h-[36px] sm:h-[40px]"
                />
              </motion.a>
            </div>
          </div>
        </div>

        {/* Col 4 - Newsletter Subscription */}
        <div className="flex flex-col gap-3 w-full md:w-[359px]">
          <span
            className="uppercase"
            style={{
              fontFamily: "Satoshi, sans-serif",
              fontWeight: 700,
              fontSize: "14px",
              color: "#F5FFFA",
              letterSpacing: "0.5px",
            }}
          >
            Subscribe to our Newsletters
          </span>
          <div className="flex flex-col gap-4 w-full">
            {/* Email Input & Subscribe Button */}
            <form
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col sm:flex-row gap-4"
            >
              <input
                type="email"
                placeholder="Enter your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                disabled={isNewsletterLoading}
                className="flex-grow h-10 px-4 py-3 rounded disabled:opacity-50"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  fontFamily: "Geist, sans-serif",
                  fontWeight: 400,
                  fontSize: "14px",
                  color: "#FEFEFF",
                  border: "none",
                  outline: "none",
                  flex: 1,
                }}
                data-testid="newsletter-input"
              />
              <motion.button
                type="submit"
                disabled={isNewsletterLoading}
                whileHover={{ scale: isNewsletterLoading ? 1 : 1.05 }}
                whileTap={{ scale: isNewsletterLoading ? 1 : 0.95 }}
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.3)",
                  fontFamily: "Geist, sans-serif",
                  fontWeight: 400,
                  fontSize: "12px",
                  color: "#ffffff",
                  padding: "12px 24px",
                  border: "none",
                  borderRadius: "4px",
                  cursor: isNewsletterLoading ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                  opacity: isNewsletterLoading ? 0.6 : 1,
                }}
                data-testid="newsletter-submit"
              >
                {isNewsletterLoading ? "Subscribing..." : "Subscribe"}
              </motion.button>
            </form>

            {/* Newsletter Description */}
            <p
              style={{
                fontFamily: "Geist, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                color: "rgba(255, 255, 255, 0.6)",
                margin: 0,
                lineHeight: "1.5",
              }}
            >
              Join the tech revolution and stay in the know. Subscribe for
              exclusive updates, offers, and a glimpse into the future
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div
        className="w-full flex flex-col md:flex-row justify-between items-center gap-6"
        data-testid="footer-bottom"
      >
        {/* Left Side - Logo & Copyright */}
        <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-center">
          {/* Vanix Logo */}
          <img
            src="https://customer-assets.emergentagent.com/job_cattle-intelligence/artifacts/4v1y8fl1_Vanixlogowhite.svg"
            alt="Vanix Logo"
            height="32"
            className="h-[32px]"
            data-testid="footer-logo"
          />

          {/* Copyright */}
          <span
            className="text-center md:text-left"
            style={{
              fontFamily: "Satoshi, sans-serif",
              fontWeight: 400,
              fontSize: "14px",
              color: "#ffffff",
            }}
          >
            © 2026 Vanix Technologies. All rights reserved.
          </span>
        </div>

        {/* Middle - Legal Links */}
        <div className="flex gap-6">
          <motion.a
            href="https://vanixtechnologies.com/privacy-policy"
            target="_blank"
            whileHover={{ scale: 1.05 }}
            style={{
              fontFamily: "Geist, sans-serif",
              fontWeight: 400,
              fontSize: "14px",
              color: "rgba(255, 255, 255, 0.6)",
              textDecoration: "none",
              transition: "color 0.3s",
            }}
            onMouseEnter={(e) =>
              (e.target.style.color = "rgba(255, 255, 255, 1)")
            }
            onMouseLeave={(e) =>
              (e.target.style.color = "rgba(255, 255, 255, 0.6)")
            }
            data-testid="privacy-link"
          >
            Privacy Policy
          </motion.a>
          <motion.a
            href="https://vanixtechnologies.com/terms-and-conditions"
            target="_blank"
            whileHover={{ scale: 1.05 }}
            style={{
              fontFamily: "Geist, sans-serif",
              fontWeight: 400,
              fontSize: "14px",
              color: "rgba(255, 255, 255, 0.6)",
              textDecoration: "none",
              transition: "color 0.3s",
            }}
            onMouseEnter={(e) =>
              (e.target.style.color = "rgba(255, 255, 255, 1)")
            }
            onMouseLeave={(e) =>
              (e.target.style.color = "rgba(255, 255, 255, 0.6)")
            }
            data-testid="terms-link"
          >
            Terms of Services
          </motion.a>
        </div>
      </div>
    </footer>
  );
};

// Latest News Section (Bento Grid)
const LatestNewsSection = () => {
  return (
    <section
      className="w-full px-4 sm:px-6 md:px-10 xl:px-16 py-12 md:py-16"
      id="news"
      data-testid="news-section"
    >
      <ScrollReveal>
        {/* Section Header */}
        <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h2
              style={{
                fontFamily: "Satoshi, sans-serif",
                fontWeight: 700,
                fontSize: "44px",
                color: "#111827",
                margin: 0,
                textAlign: "left",
              }}
              data-testid="testimonials-title"
            >
              MyBovine Insights
            </h2>

            <p
              className="max-w-2xl mx-auto"
              style={{
                fontFamily: "Satoshi, sans-serif",
                fontWeight: 400,
                fontSize: "24px",
                color: "#111827",
              }}
              data-testid="impact-metrics-desc"
            >
              The latest breakthroughs in precision livestock farming and herd
              management technology.
            </p>
          </div>
          {/* <motion.a
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
            className="inline-flex items-center text-sm font-semibold text-[#3b82f6] hover:text-blue-700 transition-colors"
            href="#"
            style={{ cursor: "pointer" }}
          >
            View all news
            <svg
              className="ml-1 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 5l7 7-7 7"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              ></path>
            </svg>
          </motion.a> */}
        </div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 h-auto md:h-[600px]">
          {/* Main Featured Article (Left Side) */}
          <motion.article
            whileHover={{ scale: 1.02, y: -4 }}
            transition={{ duration: 0.3 }}
            className="md:col-span-7 lg:col-span-8 relative group overflow-hidden rounded-3xl bg-white shadow-sm border border-slate-100 flex flex-col"
            data-testid="featured-article"
            style={{ cursor: "pointer" }}
          >
            <div className="relative h-64 md:h-full w-full overflow-hidden">
              <img
                alt="Featured News Image"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuAVBrNmg1YpAGDZfCrfqpEpQ10anWBWf4aCIdImAaymgArpZM8Rv1vikWWYPRHOLbOsjEFICQrz6HFs_yBdz5eWJEBZ9jhy7b4CRr3Nn7v2z_bs8jSdBRaXOPvvof46ICSMG0fADEUwikeJ2Z1q3n9hGsxg7j_msDXXzFc0_562JBdYBa9cQ67ougOjh7LjsPi2-MIvRr6-Cgu8uQgwtSKohbj8PuVbje2-uhHks4hKgoY7MjHj8K5KOS6x4IKHL0kY4E-_syTldPti"
              />

              {/* Gradient Overlay for Text Readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute inset-y-0 left-0 flex items-center pl-4 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white/20 hover:bg-white/40 backdrop-blur-md p-2 rounded-full text-white"
                  style={{ cursor: "pointer" }}
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M15 19l-7-7 7-7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    ></path>
                  </svg>
                </motion.button>
              </div>
              <div className="absolute inset-y-0 right-0 flex items-center pr-4 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white/20 hover:bg-white/40 backdrop-blur-md p-2 rounded-full text-white"
                  style={{ cursor: "pointer" }}
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M9 5l7 7-7 7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    ></path>
                  </svg>
                </motion.button>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 text-white">
              <div className="flex gap-1.5 mb-6">
                <div className="w-8 h-1 bg-[#3b82f6] rounded-full"></div>
                <div className="w-2 h-1 bg-white/30 rounded-full"></div>
                <div className="w-2 h-1 bg-white/30 rounded-full"></div>
              </div>
              <span className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-wider uppercase bg-[#3b82f6] text-white rounded-full">
                LATEST INNOVATION
              </span>
              <time
                className="block text-sm text-slate-300 mb-2"
                dateTime="2023-11-20"
              >
                November 20, 2025
              </time>
              <h3 className="text-2xl md:text-4xl font-bold leading-tight mb-4 group-hover:underline decoration-[#3b82f6] decoration-4 underline-offset-8">
                Real-Time Herd Tracking: How MyBovine is Eliminating Lost
                Grazing Hours
              </h3>
              <p className="hidden md:block text-slate-200 line-clamp-2 max-w-2xl">
                Our new GPS-enabled smart collars provide sub-meter accuracy,
                allowing ranchers to monitor herd health and movement patterns
                directly from their smartphone.
              </p>
            </div>
            <a
              aria-label="Read more about our vision"
              className="absolute inset-0 z-10"
              href="#"
            ></a>
          </motion.article>

          {/* Smaller Tiles Container (Right Side) */}
          <div className="md:col-span-5 lg:col-span-4 grid grid-cols-1 gap-6 h-full">
            {/* Small Tile 1 */}
            <motion.article
              whileHover={{ scale: 1.03, y: -2 }}
              transition={{ duration: 0.3 }}
              className="relative group flex flex-col justify-between p-6 rounded-3xl bg-white shadow-sm border border-slate-100"
              data-testid="news-tile-1"
              style={{ cursor: "pointer" }}
            >
              <div>
                <div className="flex justify-between items-start mb-4">
                  <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider uppercase border border-slate-200 text-slate-600 rounded-md bg-slate-50">
                    Health Tech
                  </span>
                  <time
                    className="text-xs text-slate-400 font-medium"
                    dateTime="2023-11-18"
                  >
                    Nov 18, 2025
                  </time>
                </div>
                <h4 className="text-lg font-bold text-slate-900 leading-snug group-hover:text-[#3b82f6] transition-colors">
                  Predictive Health Alerts: Identifying Sickness 48 Hours
                  Earlier
                </h4>
              </div>
              <a
                aria-label="Read more about Version 2.0"
                className="mt-4 inline-flex items-center text-sm font-medium text-slate-500 hover:text-[#0f172a]"
                href="#"
              >
                Read Story
                <svg
                  className="ml-1 w-4 h-4 transition-transform group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                  ></path>
                </svg>
              </a>
            </motion.article>

            {/* Small Tile 2 */}
            <motion.article
              whileHover={{ scale: 1.03, y: -2 }}
              transition={{ duration: 0.3 }}
              className="relative group flex flex-col justify-between p-6 rounded-3xl bg-white shadow-sm border border-slate-100"
              data-testid="news-tile-2"
              style={{ cursor: "pointer" }}
            >
              <div>
                <div className="flex justify-between items-start mb-4">
                  <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider uppercase border border-slate-200 text-slate-600 rounded-md bg-slate-50">
                    Management
                  </span>
                  <time
                    className="text-xs text-slate-400 font-medium"
                    dateTime="2023-11-15"
                  >
                    Nov 15, 2025
                  </time>
                </div>
                <h4 className="text-lg font-bold text-slate-900 leading-snug group-hover:text-[#3b82f6] transition-colors">
                  Pasture Optimization: Using AI to Map Grass Growth Cycles
                </h4>
              </div>
              <a
                aria-label="Read Summit highlights"
                className="mt-4 inline-flex items-center text-sm font-medium text-slate-500 hover:text-[#0f172a]"
                href="#"
              >
                Read Story
                <svg
                  className="ml-1 w-4 h-4 transition-transform group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                  ></path>
                </svg>
              </a>
            </motion.article>

            {/* Small Tile 3 (Dark) */}
            <motion.article
              whileHover={{ scale: 1.03, y: -2 }}
              transition={{ duration: 0.3 }}
              className="relative group flex flex-col justify-between p-6 rounded-3xl bg-slate-900 text-white shadow-sm"
              data-testid="news-tile-3"
              style={{ cursor: "pointer" }}
            >
              <div>
                <div className="flex justify-between items-start mb-4">
                  <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider uppercase border border-slate-700 text-slate-300 rounded-md bg-slate-800">
                    Global Impact
                  </span>
                  <time
                    className="text-xs text-slate-400 font-medium"
                    dateTime="2023-11-10"
                  >
                    Nov 10, 2025
                  </time>
                </div>
                <h4 className="text-lg font-bold leading-snug group-hover:text-[#3b82f6] transition-colors">
                  Sustainability Report: Reducing Methane Emissions through Data
                </h4>
              </div>
              <a
                aria-label="Read about new office"
                className="mt-4 inline-flex items-center text-sm font-medium text-slate-400 hover:text-white"
                href="#"
              >
                Read Story
                <svg
                  className="ml-1 w-4 h-4 transition-transform group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                  ></path>
                </svg>
              </a>
            </motion.article>
          </div>
        </div>
      </ScrollReveal>
    </section>
  );
};

// Technical Specifications - Figma Assets
const imgCattleMonitoring2X1 =
  "http://localhost:3845/assets/9e042f14c9d25375bd9e94cd3a0730c5249bb5c9.png";
const img202512131722352 =
  "http://localhost:3845/assets/0b6c7d623ac9230eb144e6e98fc61cd34fc67e09.png";
const imgWeightIcon =
  "http://localhost:3845/assets/328c278e856a257fadf6b2639679629ffb591ab7.svg";
const imgVoltageIcon =
  "http://localhost:3845/assets/1f7d8681c89658d5a086ef213d730ab11df85366.svg";
const imgStorageIcon =
  "http://localhost:3845/assets/c1dab3aacdeb4c3044e7ed36bd3d656afb9b9e85.svg";
const imgBatteryIcon =
  "http://localhost:3845/assets/233e412869c746807b8f864c2440e6797347c46e.svg";
const imgInfoIcon =
  "http://localhost:3845/assets/63568f08700c544200b20fb5a21d79397c77e610.svg";
const imgNetworkIcon =
  "http://localhost:3845/assets/ce32b3fe2aed08332e4315d3dbe1fb71a7d7b870.svg";
const imgNodeWeightIcon =
  "http://localhost:3845/assets/62ddd366c23fa906878e325fa86881e282003256.svg";

// Technical Specification Component (Figma design)
const TechnicalSpecification = () => {
  const [activeTab, setActiveTab] = useState("gateway");

  return (
    <div className="flex flex-col gap-6 sm:gap-8 items-start w-full">
      {/* Title */}
      <div className="flex flex-col items-center w-full">
        <h2
          className="text-[24px] sm:text-[28px] md:text-[36px] lg:text-[44px] font-bold text-center text-black"
          style={{ fontFamily: "Satoshi, sans-serif" }}
        >
          Technical{" "}
          <span
            style={{
              background: "linear-gradient(to right, #2aa366 36%, #0084d1)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Specification
          </span>
        </h2>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 sm:gap-3.5 items-center justify-center w-full">
        <button
          onClick={() => setActiveTab("gateway")}
          className="flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg transition-all text-[15px] sm:text-[17px]"
          style={{
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            cursor: "pointer",
            backgroundColor:
              activeTab === "gateway" ? "#00bd7e" : "transparent",
            color: activeTab === "gateway" ? "#f7fbff" : "#666",
            border:
              activeTab === "gateway" ? "none" : "1px solid rgba(0,0,0,0.1)",
          }}
        >
          BLE Gateway
        </button>
        <button
          onClick={() => setActiveTab("node")}
          className="flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg transition-all text-[15px] sm:text-[17px]"
          style={{
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            cursor: "pointer",
            backgroundColor: activeTab === "node" ? "#00bd7e" : "transparent",
            color: activeTab === "node" ? "#f7fbff" : "#666",
            border: activeTab === "node" ? "none" : "1px solid rgba(0,0,0,0.1)",
          }}
        >
          BLE Node
        </button>
      </div>

      {/* Content */}
      <div className="flex flex-col lg:flex-row gap-6 sm:gap-8 items-center lg:items-end w-full">
        {/* Product Image */}
        <div className="flex flex-col items-center shrink-0 w-full lg:w-auto">
          <div className="relative w-[220px] h-[240px] sm:w-[300px] sm:h-[320px] md:w-[380px] md:h-[400px] lg:w-[400px] lg:h-[420px] xl:w-[439px] xl:h-[452px]">
            {/* Green circle background */}
            <div
              className="absolute left-1/2 bottom-0 -translate-x-1/2 rounded-full"
              style={{
                backgroundColor: "#00bd7e",
                width: "75%",
                height: "75%",
              }}
            />
            {/* Product image */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0 flex items-center justify-center"
            >
              {activeTab === "gateway" ? (
                <img
                  alt="BLE Gateway Device"
                  className="relative z-10 max-w-full max-h-full object-contain drop-shadow-xl"
                  src="/BLE Gateway Device.png"
                />
              ) : (
                <img
                  alt="BLE Node Collar"
                  className="relative z-10 max-w-full max-h-full object-contain drop-shadow-xl"
                  src="/BLE Node Collar.png"
                  style={{ transform: "rotate(-27deg)" }}
                />
              )}
            </motion.div>
          </div>
        </div>

        {/* Specs Cards */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col gap-4 sm:gap-6 items-start w-full"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 w-full">
            {activeTab === "gateway" ? (
              <>
                {/* System Weight */}
                <div className="bg-white border border-black/10 flex flex-col gap-2.5 min-h-[120px] sm:min-h-[140px] md:h-[156px] p-4 sm:p-6 rounded-xl">
                  <div className="flex gap-2.5 items-center">
                    <img
                      alt=""
                      className="w-5 h-5 sm:w-6 sm:h-6"
                      src="/System Weight.svg"
                    />
                    <span
                      className="text-xs sm:text-sm font-bold uppercase tracking-wider"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "rgba(60,74,65,0.6)",
                      }}
                    >
                      System Weight
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span
                      className="text-[20px] sm:text-[24px] md:text-[32px] font-bold"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "#171b27",
                      }}
                    >
                      400 grams
                    </span>
                    <span
                      className="text-base sm:text-lg md:text-2xl font-medium"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "#8a928d",
                      }}
                    >
                      Approximately
                    </span>
                  </div>
                </div>

                {/* Voltage Supply */}
                <div className="bg-white border border-black/10 flex flex-col gap-2.5 min-h-[120px] sm:min-h-[140px] md:h-[156px] p-4 sm:p-6 rounded-xl">
                  <div className="flex gap-2.5 items-center">
                    <img
                      alt=""
                      className="w-5 h-5 sm:w-6 sm:h-6"
                      src="/Voltage Supply.svg"
                    />
                    <span
                      className="text-xs sm:text-sm font-bold uppercase tracking-wider"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "rgba(60,74,65,0.6)",
                      }}
                    >
                      Voltage Supply
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span
                      className="text-[20px] sm:text-[24px] md:text-[32px] font-bold"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "#171b27",
                      }}
                    >
                      5V Direct
                    </span>
                    <span
                      className="text-base sm:text-lg md:text-2xl font-medium"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "#8a928d",
                      }}
                    >
                      With Backup Batteries
                    </span>
                  </div>
                </div>

                {/* Data Storage */}
                <div className="bg-white border border-black/10 flex flex-col gap-2 min-h-[120px] sm:min-h-[140px] md:h-[156px] p-4 sm:p-6 rounded-xl">
                  <div className="flex gap-2 items-center">
                    <img
                      alt=""
                      className="w-5 h-5 sm:w-6 sm:h-6"
                      src="/Data Storage.svg"
                    />
                    <span
                      className="text-xs sm:text-sm font-bold uppercase tracking-wider"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "rgba(60,74,65,0.6)",
                      }}
                    >
                      Data Storage
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span
                      className="text-[16px] sm:text-[18px] md:text-[22px] font-medium"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "#000",
                      }}
                    >
                      Onboard memory ensures data retention
                    </span>
                  </div>
                </div>

                {/* Battery Life */}
                <div className="bg-white border border-black/10 flex flex-col gap-2.5 min-h-[120px] sm:min-h-[140px] p-4 sm:p-6 rounded-xl">
                  <div className="flex gap-2.5 items-center">
                    <img
                      alt=""
                      className="w-5 h-5 sm:w-6 sm:h-6"
                      src="/Battery Life.svg"
                    />
                    <span
                      className="text-xs sm:text-sm font-bold uppercase tracking-wider"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "rgba(60,74,65,0.6)",
                      }}
                    >
                      Battery Life
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 sm:gap-2 items-baseline">
                    <span
                      className="text-[28px] sm:text-[32px] md:text-[40px] font-bold"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "#00bd7e",
                      }}
                    >
                      5
                    </span>
                    <span
                      className="text-[16px] sm:text-[18px] md:text-[22.5px] font-medium"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "#000",
                      }}
                    >
                      Years Reliable Operation
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Communication Range */}
                <div className="bg-white border border-black/10 flex flex-col gap-2.5 min-h-[120px] sm:min-h-[140px] md:h-[156px] p-4 sm:p-6 rounded-xl">
                  <div className="flex gap-2.5 items-center">
                    <img
                      alt=""
                      className="w-5 h-5 sm:w-6 sm:h-6"
                      src="/Communication Range.svg"
                    />
                    <span
                      className="text-xs sm:text-sm font-bold uppercase tracking-wider"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "rgba(60,74,65,0.6)",
                      }}
                    >
                      Communication Range
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span
                      className="text-[20px] sm:text-[24px] md:text-[32px] font-bold"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "#171b27",
                      }}
                    >
                      1.5 km
                    </span>
                    <span
                      className="text-base sm:text-lg md:text-2xl font-medium"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "#8a928d",
                      }}
                    >
                      With LE Coded PHY technology
                    </span>
                  </div>
                </div>

                {/* Voltage */}
                <div className="bg-white border border-black/10 flex flex-col gap-2.5 min-h-[120px] sm:min-h-[140px] md:h-[156px] p-4 sm:p-6 rounded-xl">
                  <div className="flex gap-2.5 items-center">
                    <img
                      alt=""
                      className="w-5 h-5 sm:w-6 sm:h-6"
                      src="/Voltage Supply.svg"
                    />
                    <span
                      className="text-xs sm:text-sm font-bold uppercase tracking-wider"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "rgba(60,74,65,0.6)",
                      }}
                    >
                      Voltage
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span
                      className="text-[20px] sm:text-[24px] md:text-[32px] font-bold"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "#171b27",
                      }}
                    >
                      1.8V - 3.6V
                    </span>
                    <span
                      className="text-base sm:text-lg md:text-2xl font-medium"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "#8a928d",
                      }}
                    >
                      Optimized for 3.3V
                    </span>
                  </div>
                </div>

                {/* Node Weight */}
                <div className="bg-white border border-black/10 flex flex-col gap-2.5 min-h-[120px] sm:min-h-[140px] md:h-[156px] sm:col-span-2 p-4 sm:p-6 rounded-xl">
                  <div className="flex gap-2.5 items-center">
                    <img
                      alt=""
                      className="w-5 h-5 sm:w-6 sm:h-6"
                      src="/System Weight.svg"
                    />
                    <span
                      className="text-xs sm:text-sm font-bold uppercase tracking-wider"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "rgba(60,74,65,0.6)",
                      }}
                    >
                      Node Weight
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span
                      className="text-[20px] sm:text-[24px] md:text-[32px] font-bold"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "#171b27",
                      }}
                    >
                      9 grams
                    </span>
                    <span
                      className="text-base sm:text-lg md:text-2xl font-medium"
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        color: "#8a928d",
                      }}
                    >
                      Ultra-lightweight
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Info Bar */}
          <div className="bg-white border border-[#e6e6e6] flex items-center gap-2 sm:gap-3 p-3 sm:p-4 md:p-6 rounded-xl w-full sm:col-span-2">
            <img
              alt=""
              className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 shrink-0"
              src="/info-icon.svg"
            />
            <p
              className="text-sm sm:text-base md:text-lg lg:text-xl"
              style={{ fontFamily: "Satoshi, sans-serif", color: "#0e131e" }}
            >
              {activeTab === "gateway"
                ? "The hub of your operation, installed directly on the farm with full power redundancy."
                : "Fits comfortably around the cow's neck."}
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Product Card Component
const ProductCard = ({ image, badge, title, description, testId }) => {
  return (
    <div
      className="relative overflow-hidden flex flex-col justify-between w-full md:w-[calc(50%-18px)] h-[240px] sm:h-[300px] md:h-[340px] lg:h-[392px] rounded-[12px] md:rounded-[16px] p-4 sm:p-5 md:p-7 group cursor-pointer"
      data-testid={testId}
    >
      {/* Background image — zooms on hover, clipped by overflow-hidden */}
      <img
        src={image}
        alt=""
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)] group-hover:scale-110 pointer-events-none"
      />
      {/* Subtle dark overlay */}
      <div className="absolute inset-0 bg-black/20 pointer-events-none" />

      {/* Badge - Top Right */}
      <div className="relative flex justify-end">
        <div
          className="flex items-center"
          style={{
            padding: "10px 16px",
            borderRadius: "50px",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(30px)",
            WebkitBackdropFilter: "blur(30px)",
            gap: "12px",
          }}
          data-testid={`${testId}-badge`}
        >
          <div
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: "#2AA366",
            }}
          />
          <span
            style={{
              fontFamily: "Satoshi, sans-serif",
              fontWeight: 700,
              fontSize: "16px",
              color: "#ffffff",
              textTransform: "uppercase",
            }}
          >
            {badge}
          </span>
        </div>
      </div>

      {/* Title and Description - Bottom Right */}
      <div className="relative flex flex-col items-end text-right" style={{ gap: "12px" }}>
        <h3
          className="text-xl sm:text-2xl md:text-[36px]"
          style={{
            fontFamily: "Satoshi, sans-serif",
            fontWeight: 700,
            color: "#ffffff",
            margin: 0,
          }}
          data-testid={`${testId}-title`}
        >
          {title}
        </h3>
        <p
          className="text-sm sm:text-base md:text-[18px]"
          style={{
            fontFamily: "Satoshi, sans-serif",
            fontWeight: 400,
            color: "#ffffff",
            margin: 0,
            maxWidth: "450px",
          }}
          data-testid={`${testId}-description`}
        >
          {description}
        </p>
      </div>
    </div>
  );
};

// ChatBot Component
const ChatBot = ({ isOpen, setIsOpen }) => {
  const { showNotification } = useNotification();
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: "bot",
      text: "Hello! 👋 I'm MyBovine's AI Assistant. Ask me anything about cattle health, monitoring, or our services!",
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [cattleId, setCattleId] = useState(
    "4b6de945-ec06-4bc1-9621-f829336db32d",
  );
  const [showCattleIdInput, setShowCattleIdInput] = useState(false);
  const [remainingToday, setRemainingToday] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Chat count and limit
  const [chatCount, setChatCount] = useState(0);
  const [isLimitReached, setIsLimitReached] = useState(false);

  // Auth flow state: "register" -> "verify" -> "chat" or "sales-success"
  const [authStep, setAuthStep] = useState(() => {
    const saved = localStorage.getItem("chatbot_auth");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.verified) {
          setChatCount(parsed.chatCount || 0);
          if ((parsed.chatCount || 0) >= 5) {
            setIsLimitReached(true);
          }
          return "chat";
        }
      } catch {}
    }
    return "register";
  });
  const [registrationMode, setRegistrationMode] = useState("chat");
  const [regForm, setRegForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [otp, setOtp] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");

  // Handle registration
  const handleRegister = async (e) => {
    e.preventDefault();
    setAuthError("");
    if (
      !regForm.firstName.trim() ||
      !regForm.lastName.trim() ||
      !regForm.email.trim() ||
      !regForm.phone.trim()
    ) {
      setAuthError("All fields are required.");
      return;
    }
    setAuthLoading(true);
    try {
      const res = await fetch(
        "https://plm-apis.vanixtechnologies.com/api/llm/register",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            firstName: regForm.firstName.trim(),
            lastName: regForm.lastName.trim(),
            email: regForm.email.trim(),
            phone: regForm.phone.trim(),
          }),
        },
      );
      const data = await res.json();
      if (!res.ok) {
        setAuthError(data?.message || "Registration failed. Please try again.");
        return;
      }
      setAuthStep("verify");
    } catch (err) {
      setAuthError("Network error. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  // Handle OTP verification
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setAuthError("");
    if (!otp.trim()) {
      setAuthError("Please enter the OTP.");
      return;
    }
    setAuthLoading(true);
    try {
      const res = await fetch(
        "https://plm-apis.vanixtechnologies.com/api/llm/verify-otp",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: regForm.email.trim(),
            otp: otp.trim(),
          }),
        },
      );
      const data = await res.json();
      if (!res.ok) {
        setAuthError(data?.message || "Invalid OTP. Please try again.");
        return;
      }
      localStorage.setItem(
        "chatbot_auth",
        JSON.stringify({
          verified: true,
          email: regForm.email.trim(),
          token: data?.token || null,
          chatCount: 0,
        }),
      );
      setChatCount(0);
      setIsLimitReached(false);
      if (registrationMode === "sales-contact") {
        setAuthStep("sales-success");
        showNotification(
          "Thank you! Our sales team will contact you soon.",
          true,
        );
      } else {
        setAuthStep("chat");
        showNotification("Verified! You can now chat with Bovie.", true);
      }
    } catch (err) {
      setAuthError("Network error. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    setAuthError("");
    setAuthLoading(true);
    try {
      await fetch("https://plm-apis.vanixtechnologies.com/api/llm/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: regForm.firstName.trim(),
          lastName: regForm.lastName.trim(),
          email: regForm.email.trim(),
          phone: regForm.phone.trim(),
        }),
      });
      setOtp("");
      setAuthError("A new OTP has been sent to your email.");
    } catch {
      setAuthError("Failed to resend OTP.");
    } finally {
      setAuthLoading(false);
    }
  };

  // Handle contact sales - reset form and show registration for sales
  const handleContactSales = () => {
    setAuthStep("register");
    setRegistrationMode("sales-contact");
    setRegForm({ firstName: "", lastName: "", email: "", phone: "" });
    setOtp("");
    setAuthError("");
  };

  // Function to play cow moo sound from file
  const playMooSound = () => {
    try {
      const audio = new Audio("/Cute_Baby_Cow_Sound___Calf_Sound_effects.m4a");
      audio.volume = 0.7;
      audio.play().catch((error) => {
        console.warn("Could not play audio file:", error);
      });
    } catch (error) {
      console.warn("Error playing moo sound:", error);
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop =
          messagesContainerRef.current.scrollHeight;
      }
    }, 0);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLimitReached) return;

    // Add user message
    const userMessage = {
      id: messages.length + 1,
      type: "user",
      text: inputValue,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const response = await fetch(
        "https://sas7cahik5.execute-api.ap-south-2.amazonaws.com/dev/api/cattle/chatbot",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            cattle_id: cattleId,
            question: inputValue,
          }),
        },
      );

      const data = await response.json();

      // Extract answer from nested body structure
      const answerText =
        data?.body?.answer ||
        data?.answer ||
        data?.response ||
        data?.message ||
        "I understood your question. Please provide more details.";

      // Get remaining_today count
      const remaining = data?.body?.remaining_today;
      if (remaining !== undefined) {
        setRemainingToday(remaining);
      }

      // Increment chat count
      const newChatCount = chatCount + 1;
      setChatCount(newChatCount);

      // Update localStorage with new chat count
      const savedAuth = JSON.parse(localStorage.getItem("chatbot_auth"));
      if (savedAuth) {
        savedAuth.chatCount = newChatCount;
        localStorage.setItem("chatbot_auth", JSON.stringify(savedAuth));
      }

      // Add bot response
      const botMessage = {
        id: messages.length + 2,
        type: "bot",
        text: answerText,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botMessage]);

      // Check if limit reached
      if (newChatCount >= 5) {
        setIsLimitReached(true);
        const limitMessage = {
          id: messages.length + 3,
          type: "bot",
          text: "You've reached your free chat limit (5 conversations). To continue, please contact our sales team at sales@vanix.in or call +91-XXXX-XXXX-XX. We'd love to discuss a plan that works for you! 🐄",
          timestamp: new Date(),
        };
        setTimeout(() => {
          setMessages((prev) => [...prev, limitMessage]);
        }, 500);
      }

      // Show success notification
      // showNotification("Response received successfully!", true);
    } catch (error) {
      console.error("ChatBot error:", error);
      const errorMessage = {
        id: messages.length + 2,
        type: "bot",
        text: "Sorry, I couldn't process your request. Please try again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);

      // Show error notification
      showNotification(
        error.message || "Failed to get response. Please try again.",
        false,
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backdropFilter: "blur(25px)", backgroundColor: "rgba(0,0,0,0.45)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setIsOpen(false); }}
          data-testid="chatbot-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.25 }}
            className="relative flex flex-col w-[93vw] max-w-[593px] overflow-hidden"
            style={{
              height: "min(85vh, 680px)",
              borderRadius: "20.569px",
              borderTop: "8.999px solid #2aa366",
              background: "white",
              boxShadow: "0px 4px 4px 0px rgba(0,0,0,0.25), 0px 0px 0.643px 0px rgba(0,0,0,0.5), 0px 32.139px 64.279px 0px rgba(0,0,0,0.25)",
            }}
            data-testid="chatbot-window"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-[30px] py-[15px] shrink-0 border-b border-black/5">
              <p
                className="text-[22px] text-[#303236]"
                style={{ fontFamily: "Public Sans, Satoshi, sans-serif", fontWeight: 400 }}
              >
                Chat with Gauri
              </p>
              {authStep === "chat" && !isLimitReached && (
                <span className="text-xs text-[#9CA3AF] mr-auto ml-3" style={{ fontFamily: "Satoshi, sans-serif" }}>
                  {chatCount}/5
                </span>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="size-[30px] flex items-center justify-center hover:opacity-60 transition shrink-0"
              >
                <img
                  src="https://www.figma.com/api/mcp/asset/16434c75-e4bb-49c3-9e98-97be88e7a8aa"
                  alt="Close"
                  className="size-full object-contain"
                />
              </button>
            </div>

            {/* Auth / Chat Content */}
            {authStep === "sales-success" ? (
              /* Sales Contact Success */
              <div className="flex-1 overflow-y-auto p-6 flex flex-col items-center justify-center bg-white">
                <div className="flex flex-col items-center gap-4 text-center">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(0,189,126,0.15)" }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#00BD7E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg" style={{ color: "#303236", fontFamily: "Satoshi, sans-serif" }}>Thank You!</h4>
                    <p className="text-sm mt-2" style={{ color: "#9CA3AF", fontFamily: "Satoshi, sans-serif" }}>We've received your information and upgraded your request.</p>
                  </div>
                  <div className="w-full p-4 rounded-lg mt-4" style={{ backgroundColor: "rgba(0,189,126,0.1)", borderLeft: "4px solid #00BD7E" }}>
                    <p className="text-xs font-medium" style={{ color: "#00BD7E", fontFamily: "Satoshi, sans-serif" }}>✓ Our sales team will contact you within 24 hours at</p>
                    <p className="text-sm font-semibold mt-1" style={{ color: "#303236", fontFamily: "Satoshi, sans-serif" }}>{regForm.email}</p>
                  </div>
                  <p className="text-xs mt-4" style={{ color: "#9CA3AF", fontFamily: "Satoshi, sans-serif" }}>You can close this chat now. We'll reach out to discuss your needs!</p>
                  <button onClick={() => setIsOpen(false)} className="mt-6 w-full text-white py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 transition" style={{ backgroundColor: "#00BD7E", fontFamily: "Satoshi, sans-serif" }}>
                    Close Chat
                  </button>
                </div>
              </div>
            ) : authStep === "register" ? (
              /* Registration Form */
              <div className="flex-1 overflow-y-auto p-6 bg-white">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(0,189,126,0.1)" }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#00BD7E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                  <div className="text-center">
                    <h4 className="font-bold text-base" style={{ color: "#303236", fontFamily: "Satoshi, sans-serif" }}>Welcome to Bovi Chat</h4>
                    <p className="text-xs mt-1" style={{ color: "#9CA3AF", fontFamily: "Satoshi, sans-serif" }}>Register to start chatting with our AI assistant</p>
                  </div>
                  <form onSubmit={handleRegister} className="w-full flex flex-col gap-3 mt-2">
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <label className="text-xs font-medium block mb-1" style={{ color: "#303236" }}>First Name</label>
                        <input type="text" value={regForm.firstName} onChange={(e) => setRegForm((p) => ({ ...p, firstName: e.target.value }))} placeholder="John"
                          className="w-full border border-black/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none" style={{ color: "#303236", backgroundColor: "white", fontFamily: "Satoshi, sans-serif" }}
                          onFocus={(e) => (e.target.style.borderColor = "#00BD7E")} onBlur={(e) => (e.target.style.borderColor = "rgba(0,0,0,0.1)")} />
                      </div>
                      <div className="flex-1">
                        <label className="text-xs font-medium block mb-1" style={{ color: "#303236" }}>Last Name</label>
                        <input type="text" value={regForm.lastName} onChange={(e) => setRegForm((p) => ({ ...p, lastName: e.target.value }))} placeholder="Doe"
                          className="w-full border border-black/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none" style={{ color: "#303236", backgroundColor: "white", fontFamily: "Satoshi, sans-serif" }}
                          onFocus={(e) => (e.target.style.borderColor = "#00BD7E")} onBlur={(e) => (e.target.style.borderColor = "rgba(0,0,0,0.1)")} />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1" style={{ color: "#303236" }}>Email</label>
                      <input type="email" value={regForm.email} onChange={(e) => setRegForm((p) => ({ ...p, email: e.target.value }))} placeholder="you@example.com"
                        className="w-full border border-black/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none" style={{ color: "#303236", backgroundColor: "white", fontFamily: "Satoshi, sans-serif" }}
                        onFocus={(e) => (e.target.style.borderColor = "#00BD7E")} onBlur={(e) => (e.target.style.borderColor = "rgba(0,0,0,0.1)")} />
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1" style={{ color: "#303236" }}>Phone</label>
                      <input type="tel" value={regForm.phone} onChange={(e) => setRegForm((p) => ({ ...p, phone: e.target.value }))} placeholder="+1234567890"
                        className="w-full border border-black/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none" style={{ color: "#303236", backgroundColor: "white", fontFamily: "Satoshi, sans-serif" }}
                        onFocus={(e) => (e.target.style.borderColor = "#00BD7E")} onBlur={(e) => (e.target.style.borderColor = "rgba(0,0,0,0.1)")} />
                    </div>
                    {authError && <p className="text-xs text-center" style={{ color: "#ef4444" }}>{authError}</p>}
                    <button type="submit" disabled={authLoading} className="w-full text-white py-3 rounded-lg text-sm font-semibold hover:opacity-90 transition disabled:opacity-50" style={{ backgroundColor: "#00BD7E", fontFamily: "Satoshi, sans-serif" }}>
                      {authLoading ? "Sending OTP..." : "Continue"}
                    </button>
                  </form>
                </div>
              </div>
            ) : authStep === "verify" ? (
              /* OTP Verification */
              <div className="flex-1 overflow-y-auto p-6 bg-white">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(0,189,126,0.1)" }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#00BD7E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </div>
                  <div className="text-center">
                    <h4 className="font-bold text-base" style={{ color: "#303236", fontFamily: "Satoshi, sans-serif" }}>Verify Your Email</h4>
                    <p className="text-xs mt-1" style={{ color: "#9CA3AF", fontFamily: "Satoshi, sans-serif" }}>
                      We've sent a verification code to<br />
                      <span style={{ color: "#00BD7E", fontWeight: 600 }}>{regForm.email}</span>
                    </p>
                  </div>
                  <form
                    onSubmit={handleVerifyOtp}
                    className="w-full flex flex-col gap-3 mt-2"
                >
                  <div>
                    <label
                      className="text-xs font-medium block mb-1"
                      style={{ color: "#303236" }}
                    >
                      Enter OTP
                    </label>
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) =>
                        setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                      }
                      placeholder="000000"
                      maxLength={6}
                      className="w-full border border-black/10 rounded-lg px-3 py-3 text-center text-lg tracking-[0.5em] font-bold focus:outline-none"
                      style={{
                        color: "#303236",
                        backgroundColor: "white",
                        fontFamily: "Satoshi, sans-serif",
                        letterSpacing: "0.5em",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#00BD7E")}
                      onBlur={(e) =>
                        (e.target.style.borderColor = "rgba(0,0,0,0.1)")
                      }
                      autoFocus
                    />
                  </div>
                  {authError && (
                    <p
                      className="text-xs text-center"
                      style={{
                        color: authError.includes("sent")
                          ? "#00BD7E"
                          : "#ef4444",
                      }}
                    >
                      {authError}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={authLoading || otp.length < 4}
                    className="w-full text-white py-3 rounded-lg text-sm font-semibold hover:opacity-90 transition disabled:opacity-50"
                    style={{
                      backgroundColor: "#00BD7E",
                      fontFamily: "Satoshi, sans-serif",
                    }}
                  >
                    {authLoading ? "Verifying..." : "Verify & Start Chatting"}
                  </button>
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => {
                        setAuthStep("register");
                        setAuthError("");
                        setOtp("");
                      }}
                      className="text-xs hover:underline"
                      style={{
                        color: "#9CA3AF",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      &larr; Back
                    </button>
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={authLoading}
                      className="text-xs hover:underline disabled:opacity-50"
                      style={{
                        color: "#00BD7E",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      Resend OTP
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : (
            /* Chat Interface - Figma Design */
            <>
              {/* Cattle ID Input */}
              {showCattleIdInput && (
                <div className="px-[30px] py-3 border-b border-black/10 bg-white shrink-0">
                  <label className="text-sm font-medium block mb-2" style={{ color: "#303236" }}>Cattle ID</label>
                  <input type="text" value={cattleId} onChange={(e) => setCattleId(e.target.value)} placeholder="Enter Cattle ID"
                    className="w-full border border-black/10 rounded-lg px-3 py-2 text-sm focus:outline-none" style={{ color: "#303236", backgroundColor: "white" }}
                    onFocus={(e) => (e.target.style.borderColor = "#00BD7E")} onBlur={(e) => (e.target.style.borderColor = "rgba(0,0,0,0.1)")} />
                  <button onClick={() => setShowCattleIdInput(false)} className="mt-2 w-full text-white py-2 rounded-lg text-sm font-medium hover:opacity-90 transition" style={{ backgroundColor: "#00BD7E" }}>Done</button>
                </div>
              )}

              {/* Messages Container */}
              <div
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto px-[30px] py-[20px] flex flex-col gap-[20px] bg-white"
              >
                {/* Today label */}
                <div className="flex items-center justify-center">
                  <span className="text-[15px] text-[#a2a3a6]" style={{ fontFamily: "Satoshi, sans-serif" }}>Today</span>
                </div>

                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`flex flex-col ${message.type === "user" ? "items-end" : "items-start"} w-full`}
                  >
                    {/* Bubble */}
                    <div className="relative">
                      <div
                        className={`px-[20px] py-[20px] ${message.type === "user" ? "rounded-tl-[10px] rounded-tr-[10px] rounded-bl-[10px]" : "rounded-tl-[10px] rounded-tr-[10px] rounded-br-[10px]"} max-w-[78%]`}
                        style={{
                          backgroundColor: message.type === "user" ? "#161a21" : "#f0f4f8",
                          minWidth: "120px",
                        }}
                      >
                        <p className="text-[15px] leading-relaxed break-words" style={{ color: message.type === "user" ? "#e8e8e9" : "#303236", fontFamily: "Satoshi, sans-serif" }}>
                          {message.text}
                        </p>
                      </div>
                      {/* Message tail from Figma */}
                      {message.type === "user" ? (
                        <div className="absolute bottom-[1px] right-[-10px] h-[13px] w-[10px]">
                          <img src="https://www.figma.com/api/mcp/asset/5ae85c73-fc52-4705-95e2-b43806222857" alt="" className="absolute block w-full h-full max-w-none" />
                        </div>
                      ) : (
                        <div className="absolute bottom-[1px] left-[-10px] h-[13px] w-[10px] flex items-center justify-center">
                          <div className="-scale-y-100 rotate-180">
                            <img src="https://www.figma.com/api/mcp/asset/4718b809-dd96-4401-9287-303e026c3188" alt="" className="block h-[13px] w-[10px] max-w-none" />
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Timestamp */}
                    <span className="text-[12px] mt-1 px-1" style={{ color: "#73767a", fontFamily: "Satoshi, sans-serif" }}>
                      {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </motion.div>
                ))}

                {/* Typing Indicator */}
                {isLoading && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-start">
                    <div className="px-[20px] py-[16px] rounded-tl-[10px] rounded-tr-[10px] rounded-br-[10px]" style={{ backgroundColor: "#f0f4f8" }}>
                      <motion.div className="flex space-x-1.5" animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.5, repeat: Infinity }}>
                        <span className="w-2 h-2 rounded-full bg-[#92bfa7]"></span>
                        <span className="w-2 h-2 rounded-full bg-[#92bfa7]"></span>
                        <span className="w-2 h-2 rounded-full bg-[#92bea6]"></span>
                      </motion.div>
                    </div>
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input Area — Figma Design */}
              {!isLimitReached ? (
                <form
                  onSubmit={sendMessage}
                  className="flex gap-[10px] items-center px-[30px] py-[20px] border-t border-black/5 bg-white shrink-0"
                >
                  {/* Input box */}
                  <div className="flex-1 flex items-center gap-[12px] h-[51px] px-[20px] rounded-[8px] border border-[rgba(0,0,0,0.05)]" style={{ backgroundColor: "rgba(255,255,255,0.05)", boxShadow: "inset 0px 0px 5px 0px rgba(0,0,0,0.05)", background: "#f7f8fa" }}>
                    <input
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder="Type a message..."
                      disabled={isLoading}
                      className="flex-1 text-[15px] outline-none bg-transparent"
                      style={{ color: "#303236", fontFamily: "Satoshi, sans-serif" }}
                    />
                    {/* Mic icon from Figma */}
                    <div className="relative shrink-0 size-[20px]">
                      <img src="https://www.figma.com/api/mcp/asset/8b1be73e-120f-426c-9471-7fb25ba6c4ab" alt="" className="absolute block w-full h-full max-w-none" />
                    </div>
                  </div>
                  {/* Send button from Figma */}
                  <motion.button
                    type="submit"
                    disabled={isLoading || !inputValue.trim()}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center justify-center h-[51px] w-[51px] rounded-[10px] shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: "#2aa366" }}
                  >
                    <div className="flex items-center justify-center size-[25px]">
                      <div className="rotate-90">
                        <img src="https://www.figma.com/api/mcp/asset/228469d3-8c21-46af-8247-fa43b4826ba6" alt="Send" className="block size-[25px] max-w-none" />
                      </div>
                    </div>
                  </motion.button>
                </form>
              ) : (
                <div className="px-[30px] py-4 border-t border-black/5 bg-[#FEF3C7] shrink-0">
                  <p className="text-sm font-medium mb-3" style={{ color: "#92400E" }}>You've used all your free chats (5/5)</p>
                  <button onClick={handleContactSales} className="text-sm font-medium block w-full py-2 px-3 rounded-lg text-center text-white hover:opacity-90 transition" style={{ backgroundColor: "#F59E0B", fontFamily: "Satoshi, sans-serif" }}>
                    Contact Sales Team
                  </button>
                </div>
              )}
            </>
          )}
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

// Future Carousel - Left Column (Features List)
// Figma asset URLs for the feature section
const figmaFeatureImg1Bg = "https://www.figma.com/api/mcp/asset/d0bed903-7222-4ec3-b23c-f2bdfb3b507e";
const figmaFeatureImg1Phone = "https://www.figma.com/api/mcp/asset/62f3d04e-5238-4329-9000-1860642f1757";
const figmaFeatureImg2 = "https://www.figma.com/api/mcp/asset/eee9dd95-f199-4417-b1bf-4007f9da5a51";
const figmaFeatureImg3 = "https://www.figma.com/api/mcp/asset/34061c5e-d6af-46dc-84b3-086064b2f403";
const figmaFeatureImg4 = "https://www.figma.com/api/mcp/asset/119268b6-45ea-4d61-a902-085c11c6df13";

const PANEL_HEIGHT = 378;

const FutureCarouselLeft = ({ activeFeature, setActiveFeature }) => {
  const [isAutoPlay, setIsAutoPlay] = useState(true);

  const features = [
    {
      number: "01",
      title: "Precision Reproductive Insights",
      description:
        "Never miss a silent heat again. Our intelligent monitoring identifies the Optimal Insemination Window with vet backed accuracy. By eliminating the long data gaps found in traditional systems, we ensure your breeding cycles are always on track.",
    },
    {
      number: "02",
      title: "Early Warning Health Intelligence",
      description:
        "Detects metabolic shifts and fevers long before the first physical symptom appears. Our system identifies hidden physiological changes, allowing for immediate intervention that saves lives and slashes medical costs.",
    },
    {
      number: "03",
      title: "Personalized Behavioral Analytics",
      description:
        "Every cow is unique, and our AI knows it. The system builds a Digital Health Profile for every animal, learning her specific patterns of feeding and resting. The moment her Normal changes, you get an instant alert.",
    },
    {
      number: "04",
      title: "Seamless 24/7 Connectivity",
      description:
        "Experience total peace of mind with industrial-grade reliability. While legacy systems leave you waiting for updates, our Real-Time Data Stream ensures you are always connected to the heartbeat of your farm, anywhere in the world.",
    },
  ];

  useEffect(() => {
    if (!isAutoPlay) return;
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [isAutoPlay, features.length, setActiveFeature]);

  const handleFeatureClick = (index) => {
    setIsAutoPlay(false);
    setActiveFeature(index);
  };

  return (
    <div className="flex flex-col gap-[24px] md:gap-[32px] lg:gap-[36px] min-h-[378px] h-[520px] overflow-hidden">
      {features.map((feature, index) => {
        const isActive = index === activeFeature;
        return (
          <motion.div
            key={index}
            onClick={() => handleFeatureClick(index)}
            className="cursor-pointer w-full"
            animate={{ opacity: isActive ? 1 : 0.6 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            whileHover={!isActive ? { opacity: 0.8 } : undefined}
          >
            {/* Number + Title row */}
            <div className={`flex gap-[8px] items-baseline ${isActive ? "pb-[10px] md:pb-[16px]" : ""}`}>
              <span
                className="font-black opacity-60 shrink-0 text-[22px] sm:text-[28px] lg:text-[36px] leading-normal"
                style={{ color: "#34cc80", fontFamily: "Satoshi, sans-serif" }}
              >
                {feature.number}.
              </span>
              <h3
                className="font-medium text-black text-[14px] sm:text-[16px] md:text-[18px] xl:text-[22px] leading-[1.2]"
                style={{ fontFamily: "Satoshi, sans-serif" }}
              >
                {feature.title}
              </h3>
            </div>

            {/* Description — expand/collapse with height animation */}
            <AnimatePresence initial={false}>
              {isActive && (
                <motion.div
                  key="desc"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{
                    height: { duration: 0.45, ease: [0.4, 0, 0.2, 1] },
                    opacity: { duration: 0.3, delay: 0.08 },
                  }}
                  className="overflow-hidden"
                >
                  <p
                    className="text-[13px] sm:text-[15px] lg:text-[18px] leading-relaxed"
                    style={{ color: "rgba(0,0,0,0.6)", fontFamily: "Satoshi, sans-serif", fontWeight: 500 }}
                  >
                    {feature.description}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
};

// Future Carousel — Right Column: Figma sliding strip animation
const FutureCarouselRight = ({ activeFeature }) => {
  return (
    <div
      className="relative w-full overflow-hidden rounded-[16px] bg-[#e6e6e6]"
      style={{ height: `${PANEL_HEIGHT}px` }}
    >
      {/* Sliding strip — all 4 panels stacked, slides up by PANEL_HEIGHT × index */}
      <motion.div
        className="absolute left-0 top-0 w-full"
        style={{ height: `${PANEL_HEIGHT * 4}px` }}
        animate={{ y: -(activeFeature * PANEL_HEIGHT) }}
        transition={{ duration: 0.39, ease: [0.4, 0, 0.2, 1] }}
      >
        {/* Panel 1 — dark composite: bg cattle image + blur overlay + phone in hand */}
        <div
          className="relative w-full shrink-0 bg-[#1e293b]"
          style={{ height: `${PANEL_HEIGHT}px` }}
        >
          {/* Background cattle image */}
          <img
            src={figmaFeatureImg1Bg}
            alt=""
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            style={{ opacity: 0.8 }}
          />
          {/* Dark blur overlay */}
          <div
            className="absolute inset-0"
            style={{ backdropFilter: "blur(2px)", backgroundColor: "rgba(0,0,0,0.3)" }}
          />
          {/* Phone in hand — centered, slightly larger than container like Figma */}
          <img
            src={figmaFeatureImg1Phone}
            alt="App on phone"
            className="absolute object-cover pointer-events-none"
            style={{
              width: "100%",
              height: "512px",
              left: 0,
              top: "50%",
              transform: "translateY(-50%) translateY(10px)",
            }}
          />
        </div>

        {/* Panel 2 — farmer with phone */}
        <div className="relative w-full shrink-0" style={{ height: `${PANEL_HEIGHT}px` }}>
          <img
            src={figmaFeatureImg2}
            alt="Early Warning Health Intelligence"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          />
        </div>

        {/* Panel 3 — cattle on road */}
        <div className="relative w-full shrink-0" style={{ height: `${PANEL_HEIGHT}px` }}>
          <img
            src={figmaFeatureImg3}
            alt="Personalized Behavioral Analytics"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          />
        </div>

        {/* Panel 4 — cattle grazing */}
        <div className="relative w-full shrink-0" style={{ height: `${PANEL_HEIGHT}px` }}>
          <img
            src={figmaFeatureImg4}
            alt="Seamless 24/7 Connectivity"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          />
        </div>
      </motion.div>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBrochureModalOpen, setIsBrochureModalOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const openBrochureModal = () => setIsBrochureModalOpen(true);
  const closeBrochureModal = () => setIsBrochureModalOpen(false);

  return (
    <NotificationProvider>
      <div
        className="min-h-screen bg-white scroll-smooth"
        data-testid="landing-page"
      >
        {/* Lead Capture Modal - Farm Assessment */}
        <LeadCaptureModal isOpen={isModalOpen} onClose={closeModal} />

        {/* Brochure Download Modal */}
        <BrochureModal
          isOpen={isBrochureModalOpen}
          onClose={closeBrochureModal}
        />

        {/* Dynamic Navigation Bar */}
        <DynamicNavbar onOpenModal={openModal} />

        {/* Spacer for fixed navbar */}
        <div className="h-[64px] sm:h-[72px] md:h-[80px]" />

        {/* Hero Section */}
        <section
          id="hero-section"
          className="w-full px-4 sm:px-6 md:px-8"
          data-testid="hero-section"
        >
          <div className="relative w-full rounded-[12px] md:rounded-[16px] overflow-hidden flex flex-col items-end justify-end min-h-[70vh] md:min-h-[85vh] pb-6 md:pb-8 pt-[80px] sm:pt-[100px] md:pt-[120px] px-4 sm:px-6 md:px-8">
            {/* Background Video */}
            <video
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover z-0"
              poster="https://customer-assets.emergentagent.com/job_cattle-intelligence/artifacts/5osdznmu_GatewayIMG.png"
            >
              <source src="./assets/Mybovine hero video.mp4" type="video/mp4" />
            </video>
            {/* Dark overlay for text readability */}
            <div className="absolute inset-0 bg-black/30 z-[1] rounded-[16px]" />

            <ScrollReveal>
              <div className="relative z-[2] flex flex-col md:flex-row items-start md:items-end justify-between gap-4 sm:gap-6 md:gap-8 w-full">
                {/* Left Column - Title */}
                <div className="flex flex-col flex-1 justify-center w-full md:w-auto">
                  <h1
                    className="text-[24px] sm:text-[30px] md:text-[44px] lg:text-[58px] font-bold text-white tracking-[-1px] md:tracking-[-1.5px] leading-[1.2] md:leading-[1.25] lg:leading-[74px]"
                    style={{
                      fontFamily: "Satoshi, sans-serif",
                    }}
                    data-testid="hero-heading"
                  >
                    Predictive Intelligence. <br /> Zero-Loss Dairy.{" "}
                    <span style={{ color: "#00BD7E" }}>
                      {" "}
                      <br /> Maximum
                    </span>{" "}
                    Milk Yield.
                  </h1>
                </div>

                {/* Right Column - Description and Button */}
                <div className="flex flex-col flex-1 gap-3 sm:gap-4 items-start justify-end w-full md:w-auto">
                  {/* Subheading */}
                  <p
                    className="text-[14px] sm:text-[16px] md:text-[20px] lg:text-[24px] text-white tracking-[-0.48px]"
                    style={{
                      fontFamily: "Satoshi, sans-serif",
                      fontWeight: 400,
                      lineHeight: 1.45,
                    }}
                    data-testid="hero-subheading"
                  >
                    The AI-powered cattle intelligence platform that detects
                    silent heat and identifies early-stage illness hours before
                    symptoms appear.
                  </p>

                  {/* CTA Button Container */}
                  <div className="flex flex-col items-start w-full">
                    <div className="w-full sm:w-[260px]">
                      <div className="w-full">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={openModal}
                          className="transition-all hover:opacity-90 bg-[#00BD7E] px-4 sm:px-6 py-2.5 sm:py-3 rounded-[12px] cursor-pointer w-full text-center text-[14px] sm:text-[16px] md:text-[18px]"
                          style={{
                            fontFamily: "Satoshi, sans-serif",
                            fontWeight: 500,
                            color: "#ffffff",
                          }}
                          data-testid="book-assessment-btn"
                        >
                          Book a Farm Assessment
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>
        {/* The Future of CattleIntelligence */}
        <section
          className="w-full px-4 sm:px-6 md:px-10 xl:px-16 py-12 sm:py-16 md:py-20 lg:py-24"
          style={{ backgroundColor: "#f9fafb" }}
          data-testid="future-cattle-intelligence-section"
        >
            {/* Header Section */}
            <div className="flex flex-col items-start gap-4 sm:gap-6 mb-12 sm:mb-16 md:mb-20 max-w-full">
              {/* Title with Gradient */}
              <h2
                className="text-[28px] sm:text-[36px] md:text-[42px] lg:text-[48px] font-bold text-black leading-[1.1] max-w-full sm:max-w-[768px]"
                style={{ fontFamily: "Satoshi, sans-serif" }}
              >
                Your Partner in{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2AA366] to-[#0084D1]">
                  High-Performance
                </span>{" "}
                Dairy Growth
              </h2>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-10 lg:gap-12 w-full items-start" style={{ minHeight: "520px" }}>
              {/* Left Column - Features List */}
              <FutureCarouselLeft
                activeFeature={activeFeature}
                setActiveFeature={setActiveFeature}
              />

              {/* Right Column - Image Carousel */}
              <FutureCarouselRight activeFeature={activeFeature} />
            </div>
        </section>

        <section
          className="w-full overflow-hidden px-4 sm:px-6 md:px-10 xl:px-16 py-12 md:py-16"
          data-testid="card-software-section"
        >
          <div className="flex flex-col items-center gap-2 mb-8 md:mb-12 text-center">
            {/* <span
              style={{
                fontFamily: "Satoshi, sans-serif",
                fontWeight: 500,
                fontSize: "16px",
                color: "#000000",
                padding: "12px 16px",
                border: "1px solid rgba(0, 0, 0, 0.1)",
                borderRadius: "8px",
              }}
              data-testid="testimonials-badge"
            >
              OUR SOLUTIONS
            </span> */}
            <h2
              className="text-[28px] sm:text-[36px] md:text-[44px] font-bold text-[#111827]"
              style={{ fontFamily: "Satoshi, sans-serif" }}
              data-testid="impact-metrics-title"
            >
              Intelligence That Lives on the{" "}
              <span
                style={{
                  background: "linear-gradient(to right, #2AA366, #0084D1)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Edge.
              </span>{" "}
            </h2>
            <p
              className="max-w-2xl mx-auto text-base sm:text-lg md:text-[24px] text-[#111827]"
              style={{ fontFamily: "Satoshi, sans-serif", fontWeight: 400 }}
              data-testid="impact-metrics-desc"
            >
              A seamless ecosystem designed to think like a vet, speak like a
              farmer, and guide like a pro.
            </p>
          </div>

          <div
              className="flex flex-col md:flex-row justify-center flex-wrap pb-10"
              style={{ gap: "36px" }}
            >
              {/* Card 1 - Vanix Smart Gateway */}
              <ProductCard
                image="https://customer-assets.emergentagent.com/job_cattle-intelligence/artifacts/5osdznmu_GatewayIMG.png"
                badge="GATEWAY"
                title="Wireless Command Hub"
                description="Industrial-grade station connecting up to 100 collars across the farm."
                testId="card-gateway"
              />

              {/* Card 2 - Cattle Node */}
              <ProductCard
                image="https://customer-assets.emergentagent.com/job_cattle-intelligence/artifacts/sj7s7gsp_cattlewithnode.png"
                badge="CATTLE COLLAR"
                title="Precision Health Sensor"
                description="Ultra-durable wearable tracking every minute of health and reproductive activity."
                testId="card-cattle-node"
              />
            </div>

          <motion.div
              className="w-full rounded-[16px] bg-[#2AA366] relative overflow-hidden shadow-lg hover:shadow-2xl group"
              data-testid="card-software"
            >
              {/* Mobile layout: stack content + phone image */}
              <div className="flex flex-col lg:hidden p-8 sm:p-10 gap-6">
                {/* Text */}
                <div className="flex flex-col gap-3 text-white">
                  <h3
                    className="font-bold text-[28px] sm:text-[32px] leading-tight"
                    style={{ fontFamily: "Satoshi, sans-serif" }}
                    data-testid="card-software-title"
                  >
                    MyBovine.AI Ecosystem
                  </h3>
                  <p
                    className="text-[16px] sm:text-[17px] leading-normal"
                    style={{ fontFamily: "Satoshi, sans-serif" }}
                    data-testid="card-software-description"
                  >
                    Our <strong>mobile app</strong> leverages AI to transform raw data into{" "}
                    <strong>actionable intelligence.</strong> By using machine learning algorithms
                    to analyze herd behavior.
                  </p>
                </div>
                {/* Phone image — hidden on mobile */}
                {/* Store Buttons */}
                <div className="flex gap-6 items-center" data-testid="store-buttons">
                  <motion.a
                    whileHover={{ scale: 1.08, y: -2 }}
                    transition={{ duration: 0.2 }}
                    href="https://apps.apple.com/in/app/my-bovine-ai/id6758000909"
                    target="_blank"
                    data-testid="appstore-btn"
                  >
                    <img
                      src="https://www.figma.com/api/mcp/asset/4744365c-feca-42dd-86af-969e0f3ddbcd"
                      alt="Download on App Store"
                      className="h-[40px] w-[120px] object-contain"
                    />
                  </motion.a>
                  <motion.a
                    whileHover={{ scale: 1.08, y: -2 }}
                    transition={{ duration: 0.2 }}
                    href="https://play.google.com/store/apps/details?id=com.vanix.cattle.vanix_cattle&pcampaignid=web_share"
                    target="_blank"
                    data-testid="playstore-btn"
                  >
                    <img
                      src="https://www.figma.com/api/mcp/asset/4c609aae-fb8e-4d62-bd4d-0d05ed5797e4"
                      alt="Get it on Google Play"
                      className="h-[40px] w-[120px] object-contain"
                    />
                  </motion.a>
                </div>
              </div>

              {/* Desktop layout: exact Figma — fixed height, absolutely positioned images */}
              <div
                className="hidden lg:flex items-start p-[48px] relative"
                style={{ height: "360px" }}
              >
                {/* Left column */}
                <div className="flex flex-col justify-between h-full w-[478px] shrink-0 relative z-10">
                  <div className="flex flex-col gap-3 text-white">
                    <h3
                      className="font-bold text-[36px] leading-normal"
                      style={{ fontFamily: "Satoshi, sans-serif" }}
                      data-testid="card-software-title"
                    >
                      MyBovine.AI Ecosystem
                    </h3>
                    <p
                      className="text-[18px] leading-normal"
                      style={{ fontFamily: "Satoshi, sans-serif" }}
                      data-testid="card-software-description"
                    >
                      Our <strong>mobile app</strong> leverages AI to transform raw data into{" "}
                      <strong>actionable intelligence.</strong> By using machine learning algorithms
                      to analyze herd behavior.
                    </p>
                  </div>
                  {/* Store Buttons */}
                  <div className="flex gap-6 items-center" data-testid="store-buttons">
                    <motion.a
                      whileHover={{ scale: 1.08, y: -2 }}
                      transition={{ duration: 0.2 }}
                      href="https://apps.apple.com/in/app/my-bovine-ai/id6758000909"
                      target="_blank"
                      data-testid="appstore-btn"
                    >
                      <img
                        src="https://www.figma.com/api/mcp/asset/4744365c-feca-42dd-86af-969e0f3ddbcd"
                        alt="Download on App Store"
                        className="h-[40px] w-[120px] object-contain"
                      />
                    </motion.a>
                    <motion.a
                      whileHover={{ scale: 1.08, y: -2 }}
                      transition={{ duration: 0.2 }}
                      href="https://play.google.com/store/apps/details?id=com.vanix.cattle.vanix_cattle&pcampaignid=web_share"
                      target="_blank"
                      data-testid="playstore-btn"
                    >
                      <img
                        src="https://www.figma.com/api/mcp/asset/4c609aae-fb8e-4d62-bd4d-0d05ed5797e4"
                        alt="Get it on Google Play"
                        className="h-[40px] w-[120px] object-contain"
                      />
                    </motion.a>
                  </div>
                </div>

                {/* Combined phone + dashboard image — desktop only, zooms on hover */}
                <img
                  src="/Mobiledashboardimage.png"
                  alt="App and dashboard"
                  className="absolute object-contain pointer-events-none transition-transform duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)] group-hover:scale-105"
                  style={{
                    right: 0,
                    top: "-12px",
                    height: "420px",
                    width: "auto",
                    transformOrigin: "right center",
                  }}
                />
              </div>
            </motion.div>
        </section>

        {/* Products Section */}
        <section
          className="w-full px-4 sm:px-6 md:px-10 xl:px-16 pb-12 md:pb-16"
          data-testid="products-section"
        ></section>

        <ImpactMetricsSection />

        {/* Features Section - From Figma */}

        {/* Card Stacking Section - COMMENTED OUT */}
        {/* <CardStackingSection /> */}

        {/* Impact Metrics Section */}

        {/* Technical Specifications Section - COMMENTED OUT */}
        {/* <section
          className="w-full font-sans px-6 md:px-12 2xl:px-[64px] py-16 md:py-[120px]"
          style={{ backgroundColor: "#f7f9fa" }}
          data-testid="tech-specs-section"
        >
          <ScrollReveal>
            <TechnicalSpecification />
          </ScrollReveal>
        </section> */}

        {/* Latest News Section (MyBovine Insights) - COMMENTED OUT */}
        {/* <LatestNewsSection /> */}

        {/* Why MyBovine Section - COMMENTED OUT */}
        {/* <WhyMyBovineSection /> */}

        {/* Case Studies Section */}
        {/* <CaseStudiesSection /> */}

        {/* Meet Gauri Section - From Figma node 1903:2978 */}
        <section
          className="w-full px-4 sm:px-6 md:px-10 xl:px-16 py-[80px] md:py-[120px]"
          style={{ backgroundColor: "#FCFAF8" }}
          data-testid="chat-cta-section"
        >
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-[52px] items-center w-full">
            {/* Left Column */}
            <div className="flex flex-col items-start w-full lg:w-[510px] lg:shrink-0">
              {/* Live Now Badge */}
              <div className="bg-[#d6f5e6] border border-[rgba(0,105,71,0.1)] flex gap-2 items-center px-[17px] py-[7px] rounded-full">
                <div className="relative size-2 shrink-0">
                  <div className="absolute inset-0 bg-[#006947] opacity-75 rounded-full animate-ping" />
                  <div className="bg-[#006947] rounded-full size-2 relative" />
                </div>
                <span
                  className="text-[#006947] text-[12px] tracking-[0.6px] uppercase font-medium"
                  style={{ fontFamily: "Satoshi, sans-serif" }}
                >
                  Live Now
                </span>
              </div>

              {/* Heading */}
              <div className="pt-[10px] pr-[10px] w-full">
                <h2
                  className="font-bold text-[36px] md:text-[48px] text-black tracking-[-0.96px] leading-tight"
                  style={{ fontFamily: "Satoshi, sans-serif" }}
                >
                  Meet Gauri{" "}
                  <br />
                  Know how she's doing, anytime.
                </h2>
              </div>

              {/* Description */}
              <div className="pt-3 w-full pr-[81px]">
                <p
                  className="text-[#595c5d] text-[20px] leading-[32.5px]"
                  style={{ fontFamily: "Satoshi, sans-serif" }}
                >
                  Real-time insights about her health, activity, and behavior.
                </p>
              </div>

              {/* CTA Area */}
              <div className="pt-8 flex flex-col gap-4">
                {/* Ask Gauri Button */}
                <motion.button
                  onClick={() => setIsChatOpen(true)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  className="bg-[#00bd7e] flex gap-2 items-center justify-center px-6 py-4 rounded-[12px] w-full sm:w-[339px]"
                >
                  <span
                    className="font-medium text-[18px] md:text-[20px] text-white tracking-[-0.1px]"
                    style={{ fontFamily: "Satoshi, sans-serif" }}
                  >
                    Ask Gauri
                  </span>
                  <img
                    src="https://www.figma.com/api/mcp/asset/4e351d25-3d84-451c-92f4-2796078840bf"
                    alt=""
                    className="size-[26px] shrink-0"
                  />
                </motion.button>

              </div>
            </div>

            {/* Right Column - GIF + Speech Bubble */}
            <div className="relative w-full lg:flex-1 mt-8 lg:mt-0">
              {/* Speech Bubble */}
              <div
                className="absolute z-10 hidden sm:block"
                style={{ width: "210.951px", height: "113px", left: "40px", top: "-38px" }}
              >
                <img
                  src="https://www.figma.com/api/mcp/asset/a835d3df-bd1e-43db-be36-28e560160092"
                  alt="Speech bubble"
                  className="absolute inset-0 w-full h-full"
                />
                {/* "Hi there!" text */}
                <div className="absolute" style={{ left: "70px", top: "26px" }}>
                  <span
                    className="font-medium text-[18px] text-[#333]"
                    style={{ fontFamily: "Satoshi, sans-serif" }}
                  >
                    Hi there!
                  </span>
                </div>
                {/* Three dots */}
                <div
                  className="absolute flex gap-[4.537px] items-center"
                  style={{ left: "9.83px", top: "66.54px" }}
                >
                  <div className="bg-[rgba(0,105,71,0.4)] rounded-full size-[6.049px]" />
                  <div className="bg-[#92bfa7] rounded-full size-[6.049px]" />
                  <div className="bg-[#92bea6] rounded-full size-[6.049px]" />
                </div>
              </div>

              {/* Cow GIF */}
              <img
                src="/Cowsleeping.gif"
                alt="Sleeping cow animation"
                className="w-full h-auto object-contain rounded-[16px]"
              />

              {/* Location badge */}
              <div className="flex justify-center mt-4">
                <div className="flex gap-[8px] items-center px-[17px] py-[7px] rounded-[9999px] border border-[rgba(0,105,71,0.1)]">
                  <img
                    src="https://www.figma.com/api/mcp/asset/aed47c13-4c65-4045-9038-b7c459a56bb7"
                    alt=""
                    className="size-[24px] shrink-0"
                  />
                  <span
                    className="text-[12px] font-medium tracking-[0.6px] uppercase leading-[16px]"
                    style={{ color: "#a2a3a6", fontFamily: "Satoshi, sans-serif" }}
                  >
                    Gachibowli, Hyderabad, Telangana 500032
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ChatBot overlay */}
          <AnimatePresence>
            {isChatOpen && (
              <div className="w-full md:w-auto">
                <ChatBot isOpen={isChatOpen} setIsOpen={setIsChatOpen} />
              </div>
            )}
          </AnimatePresence>
        </section>

        {/* User Testimonials Section */}
        <section
          className="w-full px-4 sm:px-6 md:px-10 xl:px-16 py-12 md:py-16 overflow-hidden"
          data-testid="testimonials-section"
        >
          <ScrollReveal>
            {/* Header Container */}
            <div className="flex flex-col items-center gap-2 mb-8 md:mb-12">
              {/* Badge */}
              {/* <span
                style={{
                  fontFamily: "Satoshi, sans-serif",
                  fontWeight: 500,
                  fontSize: "16px",
                  color: "#000000",
                  padding: "12px 16px",
                  border: "1px solid rgba(0, 0, 0, 0.1)",
                  borderRadius: "8px",
                }}
                data-testid="testimonials-badge"
              >
                USER TESTIMONIALS
              </span> */}

              {/* Title */}
              <h2
                className="text-[28px] sm:text-[36px] md:text-[44px] font-bold text-center text-[#111827]"
                style={{ fontFamily: "Satoshi, sans-serif" }}
                data-testid="testimonials-title"
              >
                Maximize yield. <span className="gradient-text">Protect</span>{" "}
                every cow.
              </h2>

              {/* Description */}
              <p
                className="text-base sm:text-lg md:text-[24px] text-center text-[#111827] max-w-[900px]"
                style={{ fontFamily: "Satoshi, sans-serif", fontWeight: 400 }}
                data-testid="testimonials-description"
              >
                Gain the clinical-grade insights you need to make confident,
                real-time decisions for your dairy's future.
              </p>
            </div>

            {/* Testimonial Carousel */}
            <TestimonialsCarousel />
          </ScrollReveal>
        </section>

        {/* Trusted Partners / Logos Section */}
        <section
          className="w-full overflow-hidden py-[64px] bg-white"
        >
          {(() => {
            const logos = [
              { name: "VishwaGuru", logo: "/partners-logo/VG.svg" },
              { name: "Brahma Kumaris", logo: "/partners-logo/BKH.svg" },
              { name: "AWS", logo: "/partners-logo/AWS.svg" },
              { name: "NVIDIA", logo: "/partners-logo/NVDIA.svg" },
              { name: "TBIFI IIT Ropar", logo: "/partners-logo/TBIFI.svg" },
              { name: "DPIIT", logo: "/partners-logo/DPIIT.svg" },
              { name: "GTU", logo: "/partners-logo/GTU.svg" },
            ];
            const doubled = [...logos, ...logos];
            return (
              <motion.div
                className="flex items-center gap-16 md:gap-24"
                animate={{ x: ["0%", "-50%"] }}
                transition={{
                  ease: "linear",
                  duration: 35,
                  repeat: Infinity,
                }}
                style={{ width: "max-content" }}
              >
                {doubled.map((partner, index) => (
                  <img
                    key={index}
                    src={partner.logo}
                    alt={partner.name}
                    className="h-[40px] sm:h-[48px] md:h-[56px] xl:h-[96px] object-contain shrink-0"
                  />
                ))}
              </motion.div>
            );
          })()}
        </section>

        {/* Insights & Innovation */}
        <section className="w-full px-4 sm:px-6 md:px-10 xl:px-16 py-10 md:py-16 lg:py-[100px] flex flex-col items-center justify-center bg-white">
          <div className="flex flex-col items-start gap-6 md:gap-10 w-full">
            {/* Header */}
            <div className="w-full">
              <h2
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight"
                style={{ fontFamily: "Satoshi, sans-serif" }}
              >
                We Strive for{" "}
                <span
                  style={{
                    background: "linear-gradient(to right, #2aa366 56.25%, #0084d1 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Perfection
                </span>
              </h2>
            </div>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[32px] w-full">

              {/* Card 1 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="relative w-full h-[300px] sm:h-[340px] lg:h-[386px] rounded-[16px] overflow-hidden border border-[rgba(0,0,0,0.1)] shadow-[0px_4px_8px_0px_rgba(0,0,0,0.02),0px_6px_12px_0px_rgba(0,0,0,0.03)] flex flex-col items-start justify-end bg-white group cursor-pointer"
                onClick={() => window.open("https://theinterview.world/vanix-iot-collar-tracks-cattle-health-instantly/", "_blank")}
              >
                {/* Image — oversized for hover-zoom headroom */}
                <img
                  src="/Ronak.png"
                  alt="Vanix IoT Collar"
                  className="absolute inset-0 w-full h-[112%] object-cover transition-transform duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)] group-hover:scale-110 pointer-events-none"
                  style={{ top: 0 }}
                />
                {/* Bottom body */}
                <div
                  className="relative w-full flex flex-col gap-[12px] items-start justify-center p-[24px]"
                  style={{
                    background: "rgba(0,0,0,0.2)",
                  }}
                >
                  <div className="flex flex-col gap-[8px] items-start w-full">
                    <div className="bg-white flex items-center justify-center px-[12px] py-[4px] rounded-[100px]">
                      <span className="text-[12px] font-medium uppercase leading-normal" style={{ color: "#0084d1", fontFamily: "Satoshi, sans-serif" }}>News</span>
                    </div>
                    <a
                      href="https://theinterview.world/vanix-iot-collar-tracks-cattle-health-instantly/"
                      target="_blank"
                      className="text-[20px] font-medium text-white overflow-hidden"
                      style={{ fontFamily: "Satoshi, sans-serif", lineHeight: "auto", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                    >
                      Vanix IoT Collar Tracks Cattle Health Instantly
                    </a>
                  </div>
                  <span className="text-[14px] xl:text-[16px] font-medium tracking-[-0.09px] leading-[1.45] whitespace-nowrap" style={{ color: "#00bd7e", fontFamily: "Satoshi, sans-serif" }}>
                    Read More →
                  </span>
                </div>
              </motion.div>

              {/* Card 2 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="relative w-full h-[300px] sm:h-[340px] lg:h-[386px] rounded-[16px] overflow-hidden border border-[rgba(0,0,0,0.1)] shadow-[0px_4px_8px_0px_rgba(0,0,0,0.02),0px_6px_12px_0px_rgba(0,0,0,0.03)] flex flex-col items-start justify-end bg-white group cursor-pointer"
                onClick={() => navigate("/blog-2")}
              >
                <img
                  src="/selective-focus-shot-of-a-herd-of-brown-and-white-2026-03-17-21-46-35-utc.jpeg"
                  alt="Shanti Sarovar Case Study"
                  className="absolute inset-0 w-full h-[112%] object-cover transition-transform duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)] group-hover:scale-110 pointer-events-none"
                  style={{ top: 0 }}
                />
                <div
                  className="relative w-full flex flex-col gap-[12px] items-start justify-center p-[24px]"
                  style={{
                    background: "rgba(0,0,0,0.2)",
                  }}
                >
                  <div className="flex flex-col gap-[8px] items-start w-full">
                    <div className="bg-white flex items-center justify-center px-[12px] py-[4px] rounded-[100px]">
                      <span className="text-[12px] font-medium uppercase leading-normal" style={{ color: "#0084d1", fontFamily: "Satoshi, sans-serif" }}>Blog</span>
                    </div>
                    <button
                      onClick={() => navigate("/blog-1")}
                      className="text-[20px] font-medium text-white text-left bg-transparent border-none cursor-pointer p-0 w-full"
                      style={{ fontFamily: "Satoshi, sans-serif", lineHeight: "auto", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                    >
                      Shanti Sarovar - This is a successfully deployed case study
                    </button>
                  </div>
                  <span className="text-[14px] xl:text-[16px] font-medium tracking-[-0.09px] leading-[1.45] whitespace-nowrap" style={{ color: "#00bd7e", fontFamily: "Satoshi, sans-serif" }}>
                    Read More →
                  </span>
                </div>
              </motion.div>

              {/* Card 3 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                className="relative w-full h-[300px] sm:h-[340px] lg:h-[386px] rounded-[16px] overflow-hidden border border-[rgba(0,0,0,0.1)] shadow-[0px_4px_8px_0px_rgba(0,0,0,0.02),0px_6px_12px_0px_rgba(0,0,0,0.03)] flex flex-col items-start justify-end bg-white group cursor-pointer sm:col-span-2 lg:col-span-1"
                onClick={() => navigate("/case-study-1")}
              >
                <img
                  src="/shanti-case-1.svg"
                  alt="Case Study"
                  className="absolute inset-0 w-full h-[112%] object-cover transition-transform duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)] group-hover:scale-110 pointer-events-none"
                  style={{ top: 0 }}
                />
                <div
                  className="relative w-full flex flex-col gap-[12px] items-start justify-center p-[24px]"
                  style={{
                    background: "rgba(0,0,0,0.2)",
                  }}
                >
                  <div className="flex flex-col gap-[8px] items-start w-full">
                    <div className="bg-white flex items-center justify-center px-[12px] py-[4px] rounded-[100px]">
                      <span className="text-[12px] font-medium uppercase leading-normal" style={{ color: "#0084d1", fontFamily: "Satoshi, sans-serif" }}>Case Study</span>
                    </div>
                    <button
                      onClick={() => navigate("/case-study-1")}
                      className="text-[20px] font-medium text-white text-left bg-transparent border-none cursor-pointer p-0 w-full"
                      style={{ fontFamily: "Satoshi, sans-serif", lineHeight: "auto", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                    >
                      Shanti Sarovar - This is a successfully deployed case study
                    </button>
                  </div>
                  <span className="text-[14px] xl:text-[16px] font-medium tracking-[-0.09px] leading-[1.45] whitespace-nowrap" style={{ color: "#00bd7e", fontFamily: "Satoshi, sans-serif" }}>
                    Read More →
                  </span>
                </div>
              </motion.div>

            </div>
          </div>
        </section>

        {/* FAQ / Support Centre Section */}
        <FAQSection />

        {/* Let's Talk Section */}
        <LetsTalkSection onOpenModal={openModal} />

        {/* Footer */}
        <Footer />
      </div>
    </NotificationProvider>
  );
};

export default LandingPage;

{
  /* Software Card - Full Bleed */
}

{
  /* The Problem Section - COMMENTED OUT */
}
{
  /* <section
          className="w-full px-6 md:px-12 2xl:px-[64px] py-12 md:py-20"
          style={{ backgroundColor: "#f7f9fa" }}
          data-testid="problem-section"
        >
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
            {/* Left Column - Sticky Header */
}
{
  /* <div className="w-full lg:flex-1 lg:sticky lg:top-[80px] flex flex-col gap-11">
              <div className="flex flex-col gap-2 items-start">
                {/* Badge */
}
{
  /* <span
                  className="inline-flex items-center justify-center px-4 py-3 rounded-[12px] border border-black/10 text-[16px] font-medium tracking-[-0.32px] uppercase text-black whitespace-nowrap"
                  style={{ fontFamily: "Satoshi, sans-serif" }}
                  data-testid="problem-badge"
                >
                  THE PROBLEM
                </span>

                {/* Title */
}
{
  /* <h2
                  className="text-[28px] sm:text-[36px] md:text-[44px] font-bold text-black tracking-[-0.88px]"
                  style={{ fontFamily: "Satoshi, sans-serif", lineHeight: 1.1 }}
                  data-testid="problem-title"
                >
                  Hidden <span style={{ color: "#F44B54" }}>Losses</span> in
                  Dairy Operations
                </h2>

                {/* Subtitle */
}
{
  /* <p
                  className="text-base sm:text-lg md:text-[24px] text-black tracking-[-0.48px]"
                  style={{
                    fontFamily: "Satoshi, sans-serif",
                    fontWeight: 400,
                    lineHeight: 1.45,
                  }}
                  data-testid="problem-subtitle"
                >
                  Most dairy losses are invisible until it is too late.
                </p>
              </div>

              {/* Quote Box */
}
{
  /* <div
                className="bg-[#fafafa] border border-[#e6e6e6] rounded-[12px] p-6 w-full"
                data-testid="problem-quote"
              >
                <p
                  className="text-[#0e131e] text-[20px]"
                  style={{ fontFamily: "Satoshi, sans-serif" }}
                >
                  <span className="font-bold">
                    Without digital monitoring,{" "}
                  </span>
                  <span className="font-normal">
                    farms operate reactively instead of predictively — losing
                    time, money, and animal welfare.
                  </span>
                </p>
              </div>
            </div>

            {/* Right Column - Numbered Cards */
}
{
  /* <div className="w-full lg:flex-1" data-testid="problem-cards">
              <div className="flex flex-wrap gap-4 md:gap-10 justify-center">
                {[
                  {
                    num: "01",
                    title: "Late Illness Detection",
                    desc: "Illness is only spotted after a noticeable milk drop — costly days after onset.",
                    testId: "problem-card-1",
                  },
                  {
                    num: "02",
                    title: "Missed Heat Cycles",
                    desc: "Undetected estrus events reduce breeding efficiency and extend calving intervals.",
                    testId: "problem-card-2",
                  },
                  {
                    num: "03",
                    title: "Rising Vet Expenses",
                    desc: "Reactive treatment rather than prevention drives up veterinary costs over time.",
                    testId: "problem-card-3",
                  },
                  {
                    num: "04",
                    title: "Labor-Dependent Monitoring",
                    desc: "Herd health depends on manual observation — inconsistent and hard to scale.",
                    testId: "problem-card-4",
                  },
                  {
                    num: "05",
                    title: "No Real-Time Visibility",
                    desc: "Farmers lack live insight into individual animal status across the whole herd.",
                    testId: "problem-card-5",
                  },
                  {
                    num: "06",
                    title: "Fragmented Data",
                    desc: "Records are scattered, incomplete, or never captured — making trends invisible.",
                    testId: "problem-card-6",
                  },
                ].map((card) => (
                  <motion.div
                    key={card.num}
                    whileHover={{ scale: 1.02, y: -4 }}
                    transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                    className="bg-white border border-white/10 rounded-[12px] p-6 w-full md:flex-1 md:min-w-[280px] md:max-w-[calc(50%-20px)]"
                    data-testid={card.testId}
                  >
                    <div className="flex flex-col gap-2">
                      <p
                        className="text-[36px] font-black"
                        style={{
                          fontFamily: "Satoshi, sans-serif",
                          color: "rgba(244, 75, 84, 0.2)",
                        }}
                      >
                        {card.num}
                      </p>
                      <div className="flex flex-col gap-1 text-[#0e131e]">
                        <p
                          className="text-[20px] font-bold"
                          style={{ fontFamily: "Satoshi, sans-serif" }}
                        >
                          {card.title}
                        </p>
                        <p
                          className="text-[14px] font-normal"
                          style={{ fontFamily: "Satoshi, sans-serif" }}
                        >
                          {card.desc}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section> */
}
